import { KurooApiError, KurooClient } from "@kuroo/sdk";
import type { AvailableSlot, Booking, BookingCheckout, BookingExtraSelection, PaymentLinkStatusResponse, PriceQuote, PriceQuoteBreakdown, RecurringFrequency, RecurringOccurrence, RecurringRule, RecurringSeriesCheckout, RecurringSeriesPreview, ServicePaymentMode, ServiceSummary, SlotHoldResult } from "@kuroo/contracts";
import type { CatalogExtra, CatalogLocation, CatalogProvider } from "@kuroo/sdk";
import { BOOKING_ELEMENT_NAME, PRODUCT_NAME } from "@kuroo/brand";
export { KurooConciergeElement, defineKurooConcierge } from "./concierge";

declare global {
  interface Window {
    KurooCrmConfig?: { apiBaseUrl?: string };
  }
}

const elementName = BOOKING_ELEMENT_NAME;

type BookingStep = "datetime" | "recurrence" | "information" | "payment" | "confirmation";

const bookingSteps: Array<{ key: BookingStep; label: string }> = [
  { key: "datetime", label: "Fecha y hora" },
  { key: "recurrence", label: "Repetición" },
  { key: "information", label: "Tus datos" },
  { key: "payment", label: "Revisión y pago" }
];

const stepTitles: Record<BookingStep, string> = {
  datetime: "Fecha y hora",
  recurrence: "Repetir reserva",
  information: "Tus datos",
  payment: "Revisión y pago",
  confirmation: "Reserva confirmada"
};

function escapeHtml(value: string): string {
  return String(value).replace(/[&<>\"']/g, (character) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#039;" })[character] ?? character);
}

function localDateValue(date = new Date()): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function monthValue(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
}

function monthStart(value: string): Date {
  const [year, month] = value.split("-").map(Number);
  return new Date(year || new Date().getFullYear(), (month || 1) - 1, 1);
}

type ZonedDateTimeFields = { date: string; time: string };

function zonedDateTimeFields(value: string | Date, timezone: string): ZonedDateTimeFields | null {
  const date = typeof value === "string" ? new Date(value) : value;
  if (Number.isNaN(date.getTime())) return null;
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: timezone,
      hourCycle: "h23",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit"
    }).formatToParts(date);
    const valueFor = (type: string) => parts.find((part) => part.type === type)?.value ?? "";
    const result = {
      date: `${valueFor("year")}-${valueFor("month")}-${valueFor("day")}`,
      time: `${valueFor("hour")}:${valueFor("minute")}`
    };
    return result.date.includes("-") && !result.date.startsWith("--") && result.time.includes(":") ? result : null;
  } catch {
    return null;
  }
}

function zonedDateTimeToIso(dateValue: string, timeValue: string, timezone: string): string | null {
  const dateMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateValue);
  const timeMatch = /^(\d{2}):(\d{2})$/.exec(timeValue);
  if (!dateMatch || !timeMatch) return null;
  const [, yearValue, monthValue, dayValue] = dateMatch;
  const [, hourValue, minuteValue] = timeMatch;
  const year = Number(yearValue); const month = Number(monthValue); const day = Number(dayValue);
  const hour = Number(hourValue); const minute = Number(minuteValue);
  if (month < 1 || month > 12 || day < 1 || day > 31 || hour > 23 || minute > 59) return null;
  const wall = new Date(0);
  wall.setUTCFullYear(year, month - 1, day);
  wall.setUTCHours(hour, minute, 0, 0);
  if (wall.getUTCFullYear() !== year || wall.getUTCMonth() !== month - 1 || wall.getUTCDate() !== day || wall.getUTCHours() !== hour || wall.getUTCMinutes() !== minute) return null;
  const wallMilliseconds = wall.getTime();
  let candidate = wallMilliseconds;
  for (let attempt = 0; attempt < 5; attempt += 1) {
    const parts = zonedDateTimeFields(new Date(candidate), timezone);
    if (!parts) return null;
    const displayed = zonedDateTimeToWallMilliseconds(parts.date, parts.time);
    if (displayed === null) return null;
    candidate += wallMilliseconds - displayed;
  }
  const resolved = zonedDateTimeFields(new Date(candidate), timezone);
  return resolved?.date === dateValue && resolved.time === timeValue ? new Date(candidate).toISOString() : null;
}

function zonedDateTimeToWallMilliseconds(dateValue: string, timeValue: string): number | null {
  const dateMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateValue);
  const timeMatch = /^(\d{2}):(\d{2})$/.exec(timeValue);
  if (!dateMatch || !timeMatch) return null;
  const wall = new Date(0);
  wall.setUTCFullYear(Number(dateMatch[1]), Number(dateMatch[2]) - 1, Number(dateMatch[3]));
  wall.setUTCHours(Number(timeMatch[1]), Number(timeMatch[2]), 0, 0);
  return wall.getTime();
}

function randomIdempotency(prefix: string): string {
  try { return `${prefix}-${crypto.randomUUID()}`; }
  catch { return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2)}`; }
}

function isAbortError(error: unknown): boolean {
  return error instanceof KurooApiError && error.code === "REQUEST_ABORTED"
    || error instanceof DOMException && error.name === "AbortError"
    || error instanceof Error && (error.name === "AbortError" || /\babort(?:ed|ing)?\b/i.test(error.message));
}

type PendingHold = SlotHoldResult & { startAt: string; endAt: string; providerId?: string; locationId?: string };

export class KurooBookingElement extends HTMLElement {
  static get observedAttributes(): string[] {
    return ["api-base-url", "service-id", "provider-id", "location-id", "locale", "theme"];
  }

  private readonly root = this.attachShadow({ mode: "open" });
  private client?: KurooClient;
  private pendingHold?: PendingHold;
  private selectedSlot?: AvailableSlot;
  private recurringPreview?: RecurringSeriesPreview;
  private recurringCheckout?: RecurringSeriesCheckout;
  private bookingCheckout?: BookingCheckout;
  private bookingPaymentStatus?: PaymentLinkStatusResponse;
  private selectedDate = localDateValue();
  private calendarMonth = monthValue(new Date());
  private currentStep: BookingStep = "datetime";
  private lastBooking?: Booking;
  private catalogAbort?: AbortController;
  private slotsAbort?: AbortController;
  private calendarAbort?: AbortController;
  private recurringPreviewAbort?: AbortController;
  private serviceExtrasAbort?: AbortController;
  private catalogRequestSequence = 0;
  private requestSequence = 0;
  private calendarRequestSequence = 0;
  private recurringPreviewRequestSequence = 0;
  private serviceExtrasRequestSequence = 0;
  private countdownTimer?: number;
  private paymentPollTimer?: number;
  private recurringPreviewDebounceTimer?: number;
  private reviewQuoteAbort?: AbortController;
  private pricePulseTimer?: number;
  private paymentPollAttempts = 0;
  private layoutResizeObserver?: ResizeObserver;
  private layoutResizeFrame?: number;
  private layoutResizePendingWidth?: number;
  /** Prevents the manual action and the background poller from issuing
   * overlapping status requests. The old flow silently re-rendered the same
   * pending card, which made the button look inert when Mercado Pago had not
   * delivered its webhook yet. */
  private paymentRefreshing = false;
  private availabilityLoading = false;
  private recurringPricingLoading = false;
  private oneOffQuoteLoading = false;
  private reviewRefreshPending = false;
  private oneOffQuote?: PriceQuote;
  private oneOffQuoteRequestSequence = 0;
  private services: ServiceSummary[] = [];
  private providers: CatalogProvider[] = [];
  private locations: CatalogLocation[] = [];
  private extras: CatalogExtra[] = [];
  private availabilityDays = new Map<string, number>();
  private weeklyDaysCustomized = false;

  attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null): void {
    if (!this.isConnected || oldValue === newValue) return;
    if (name === "api-base-url") this.client = new KurooClient({ baseUrl: newValue ?? window.KurooCrmConfig?.apiBaseUrl ?? window.location.origin });
    if (name === "service-id") this.currentStep = "datetime";
    if (["api-base-url", "service-id", "provider-id", "location-id", "locale", "theme"].includes(name)) {
      this.cancelRecurringPreviewRequest({ clearPreview: true });
      this.render();
      void this.loadCatalogAndSlots();
    }
  }

  connectedCallback(): void {
    this.currentStep = "datetime";
    this.selectedDate = localDateValue();
    this.calendarMonth = monthValue(new Date());
    this.client = new KurooClient({ baseUrl: this.getAttribute("api-base-url") ?? window.KurooCrmConfig?.apiBaseUrl ?? window.location.origin });
    this.render();
    this.observeResponsiveLayout();
    void this.loadCatalogAndSlots();
  }

  disconnectedCallback(): void {
    this.catalogAbort?.abort();
    this.slotsAbort?.abort();
    this.calendarAbort?.abort();
    this.cancelRecurringPreviewRequest({ clearPreview: true });
    this.serviceExtrasAbort?.abort();
    this.reviewQuoteAbort?.abort();
    this.layoutResizeObserver?.disconnect();
    this.layoutResizeObserver = undefined;
    if (this.layoutResizeFrame !== undefined) window.cancelAnimationFrame(this.layoutResizeFrame);
    this.layoutResizeFrame = undefined;
    this.layoutResizePendingWidth = undefined;
    if (this.countdownTimer) window.clearInterval(this.countdownTimer);
    if (this.paymentPollTimer) window.clearInterval(this.paymentPollTimer);
    void this.releasePendingHold();
  }

  private observeResponsiveLayout(): void {
    this.layoutResizeObserver?.disconnect();
    this.layoutResizeObserver = undefined;
    if (this.layoutResizeFrame !== undefined) window.cancelAnimationFrame(this.layoutResizeFrame);
    this.layoutResizeFrame = undefined;
    this.layoutResizePendingWidth = undefined;
    if (typeof ResizeObserver === "undefined") return;

    const updateLayout = (width: number): void => {
      if (!Number.isFinite(width) || width <= 0) return;
      const layout = width <= 480 ? "narrow" : width <= 720 ? "compact" : "wide";
      if (this.dataset.layout !== layout) this.dataset.layout = layout;
    };

    updateLayout(this.getBoundingClientRect().width);
    const scheduleLayoutUpdate = (width: number): void => {
      if (!Number.isFinite(width) || width <= 0) return;
      this.layoutResizePendingWidth = width;
      if (this.layoutResizeFrame !== undefined) return;
      this.layoutResizeFrame = window.requestAnimationFrame(() => {
        this.layoutResizeFrame = undefined;
        const pendingWidth = this.layoutResizePendingWidth;
        this.layoutResizePendingWidth = undefined;
        if (pendingWidth !== undefined) updateLayout(pendingWidth);
      });
    };
    this.layoutResizeObserver = new ResizeObserver((entries) => {
      scheduleLayoutUpdate(entries[0]?.contentRect.width ?? this.getBoundingClientRect().width);
    });
    this.layoutResizeObserver.observe(this);
  }

  private render(): void {
    const serviceId = this.getAttribute("service-id") ?? "";
    const accent = this.getAttribute("theme") === "dark" ? "#22D3EE" : "#1F5A87";
    const defaultMonth = monthStart(this.calendarMonth);
    const monthLabel = new Intl.DateTimeFormat(this.getAttribute("locale") ?? "es-ES", { month: "long", year: "numeric" }).format(defaultMonth);
    const weekdayLabels = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];
    this.root.innerHTML = `<style>
      :host { display:block; width:100%; max-width:100%; min-width:0; color:#1A2C37; font:14px/1.45 "Montserrat",Inter,ui-sans-serif,system-ui,sans-serif; container-type:inline-size; }
      *, *::before, *::after { box-sizing:border-box; min-width:0; }
      button, input, select, textarea { font:inherit; }
      button { cursor:pointer; }
      button:disabled { cursor:not-allowed; opacity:.56; }
       .booking-shell { display:grid; grid-template-columns:240px minmax(0,1fr); width:100%; max-width:760px; min-width:0; height:560px; min-height:560px; overflow:hidden; border-radius:0 10px 10px 0; background:#fff; box-shadow:0 19px 45px rgba(26,44,55,.16); }
      .sidebar { display:flex; min-width:0; flex-direction:column; padding:1rem; color:#fff; background:#17295A; }
      .brand { display:flex; align-items:center; gap:.65rem; min-height:2.3rem; padding:.25rem .25rem 1.15rem; font-size:1rem; font-weight:750; letter-spacing:.01em; }
      .brand-mark { display:grid; place-items:center; width:1.7rem; height:1.7rem; border:1px solid rgba(255,255,255,.55); border-radius:.45rem; font-size:.8rem; }
      .steps { display:grid; gap:.45rem; margin:0; padding:0; list-style:none; }
      .step { min-width:0; }
      .step-button { display:flex; align-items:center; gap:.65rem; width:100%; padding:.65rem .55rem; border:1px solid transparent; border-radius:.35rem; color:#e8eefc; background:rgba(255,255,255,.055); text-align:left; }
      .step-button:hover:not(:disabled) { background:rgba(255,255,255,.12); }
      .step.active .step-button { background:rgba(255,255,255,.1); }
      .step.complete .step-button { color:#fff; }
      .step-index { display:grid; flex:0 0 1.45rem; place-items:center; width:1.45rem; height:1.45rem; border:2px solid rgba(255,255,255,.18); border-radius:50%; color:rgba(255,255,255,.45); font-size:.72rem; font-weight:750; }
      .step.active .step-index { border-color:${accent}; color:${accent}; box-shadow:inset 0 0 0 3px rgba(23,41,90,.92); background:#fff; }
      .step.complete .step-index { border-color:#019719; color:#fff; background:#019719; }
      .step-copy { display:grid; min-width:0; gap:.08rem; }
      .step-copy strong { overflow:hidden; font-size:.78rem; font-weight:700; text-overflow:ellipsis; white-space:nowrap; }
      .step-copy small { overflow:hidden; color:rgba(255,255,255,.62); font-size:.68rem; text-overflow:ellipsis; white-space:nowrap; }
      .sidebar-contact { margin-top:auto; padding:1rem .25rem .9rem; border-top:1px solid rgba(255,255,255,.14); color:rgba(255,255,255,.74); text-align:center; }
      .sidebar-contact strong { display:block; margin-bottom:.2rem; color:#fff; font-size:.82rem; }
      .sidebar-contact small { display:block; font-size:.72rem; }
      .collapse-button { display:flex; align-items:center; justify-content:space-between; width:100%; padding:.7rem 0 0; border:0; border-top:1px solid rgba(255,255,255,.14); color:#fff; background:transparent; font-weight:700; text-align:left; }
      .collapse-icon { display:grid; place-items:center; width:1.15rem; height:1.15rem; border-radius:50%; color:#17295A; background:#fff; font-size:.75rem; }
       .booking-main { display:flex; min-width:0; height:560px; min-height:560px; flex-direction:column; background:#fff; }
      .booking-header { display:flex; align-items:center; gap:.8rem; min-height:68px; padding:1rem 1.65rem; border-bottom:1px solid #e2e6e8; box-shadow:0 2px 5px rgba(26,44,55,.08); }
      .back-button { display:grid; flex:0 0 1.55rem; place-items:center; width:1.55rem; height:1.55rem; padding:0; border:1px solid #b9c1c5; border-radius:.35rem; color:#1A2C37; background:#fff; font-size:1.1rem; line-height:1; }
      .header-copy { min-width:0; }
      .header-eyebrow { margin:0 0 .05rem; color:${accent}; font-size:.63rem; font-weight:800; letter-spacing:.13em; text-transform:uppercase; }
      .booking-header h2 { margin:0; color:#33434C; font-size:1.17rem; font-weight:600; letter-spacing:-.02em; }
       .booking-body { flex:1; min-width:0; min-height:0; overflow-x:hidden; overflow-y:auto; overscroll-behavior:contain; padding:1.15rem 1.65rem 1.4rem; }
       .step-panel[hidden], .filter-grid[hidden], [data-service-choice][hidden], [data-customer][hidden], [data-hold][hidden] { display:none; }
       .step-panel { width:100%; max-width:520px; min-width:0; margin:0 auto; }
      .step-panel h3 { margin:0 0 .45rem; color:#33434C; font-size:1.03rem; font-weight:700; }
      .step-intro { margin:0 0 1rem; color:#66757d; font-size:.83rem; }
       .status { min-height:1.25rem; margin:0 auto .55rem; color:#52636c; font-size:.78rem; }
       .status.error, .alert { color:#8d2018; }
       .status.busy { display:flex; align-items:center; gap:.45rem; color:#075985; }
       .spinner { display:inline-block; width:.86rem; height:.86rem; border:2px solid currentColor; border-right-color:transparent; border-radius:50%; animation:kuroo-spin .75s linear infinite; vertical-align:-.12em; }
       @keyframes kuroo-spin { to { transform:rotate(360deg); } }
       .loading-skeleton { display:grid; gap:.55rem; padding:.8rem; border:1px solid #dbe1e4; border-radius:.45rem; background:#fafbfb; }
       .loading-skeleton span { display:block; height:.72rem; border-radius:.3rem; background:linear-gradient(90deg,#edf0f1 25%,#f8fafb 50%,#edf0f1 75%); background-size:200% 100%; animation:kuroo-shimmer 1.2s ease-in-out infinite; }
       @keyframes kuroo-shimmer { to { background-position:-200% 0; } }
       .alert { padding:.65rem .75rem; border:1px solid #f0bcb6; border-radius:.45rem; background:#fff5f4; }
       .service-choice { display:grid; gap:.45rem; padding:1rem; border:1px solid #d1d5d7; border-radius:.45rem; background:#fff; }
       .catalog-retry { margin-top:.65rem; text-align:right; }
       .catalog-retry button { padding:.5rem .7rem; border:1px solid #b9c1c5; border-radius:.35rem; color:#33434C; background:#fff; font-size:.75rem; font-weight:700; }
      .service-choice label, .filter-field, .field { display:grid; gap:.35rem; color:#33434C; font-size:.76rem; font-weight:650; }
       .service-choice select, .filter-field select, .field input, .field textarea { width:100%; max-width:100%; min-width:0; padding:.7rem .75rem; border:1px solid #d1d5d7; border-radius:.38rem; color:#1A2C37; background:#fff; outline:none; }
      .service-choice select:focus, .filter-field select:focus, .field input:focus, .field textarea:focus { border-color:${accent}; box-shadow:0 0 0 3px rgba(31,90,135,.12); }
       .service-meta { display:grid; grid-template-columns:minmax(0,1fr) auto; align-items:end; gap:.18rem 1rem; padding-top:.75rem; border-top:1px solid #edf0f1; color:#6c7b82; font-size:.74rem; }
       .service-payment-summary { color:#1f5a87; font-size:.68rem; font-weight:750; }
      .service-meta strong { color:#33434C; }
       .filter-grid { display:grid; grid-template-columns:repeat(2,minmax(0,1fr)); gap:.65rem; min-width:0; margin-bottom:1rem; }
       .filter-field:first-child { grid-column:1/-1; }
       .service-filter select { font-weight:700; }
      .calendar-card { padding:.1rem 0 0; }
      .calendar-toolbar { display:grid; grid-template-columns:minmax(0,1fr) minmax(0,1fr) auto auto; gap:.65rem; align-items:center; min-width:0; margin-bottom:1rem; }
      .calendar-toolbar select { width:100%; max-width:100%; min-width:0; padding:.65rem .7rem; border:1px solid #d1d5d7; border-radius:.38rem; color:#33434C; background:#fff; }
      .month-button { display:grid; place-items:center; width:2.35rem; height:2.35rem; padding:0; border:1px solid #b9c1c5; border-radius:.38rem; color:#33434C; background:#fff; font-size:1.25rem; line-height:1; }
      .calendar-week, .calendar-grid { display:grid; grid-template-columns:repeat(7,minmax(0,1fr)); gap:.4rem; min-width:0; }
      .calendar-week { margin-bottom:.4rem; color:#6c7b82; font-size:.72rem; text-align:center; }
       .calendar-day { display:grid; place-items:center; min-height:2.25rem; padding:.2rem .15rem; border:1px solid #6b9bc2; border-radius:.3rem; color:#1f5a87; background:#f0f4f7; font-size:.86rem; }
      .calendar-day:hover:not(:disabled) { background:#dfeaf1; }
      .calendar-day.outside { border-color:#e4e7e9; color:#b6c1c6; background:#fafafa; }
      .calendar-day.past { border-color:#e4e7e9; color:#c2c9cd; background:#f7f8f8; }
      .calendar-day.unavailable { border-color:#e3d6d5; color:#b1a3a3; background:#fbf1f0; }
      .calendar-day.selected { border-color:${accent}; color:#fff; background:${accent}; box-shadow:inset 0 -3px 0 rgba(0,0,0,.12); }
      .calendar-day.today span { font-weight:800; text-decoration:underline; text-underline-offset:.17rem; }
      .calendar-day small { min-height:.65rem; color:inherit; font-size:.56rem; line-height:1; }
      .calendar-note { margin:.7rem 0 0; color:#78868d; font-size:.7rem; }
      .slots-heading { display:flex; align-items:center; justify-content:space-between; gap:.75rem; margin:1.2rem 0 .5rem; }
      .slots-heading strong { color:#33434C; font-size:.83rem; }
      .next-button { padding:0; border:0; color:${accent}; background:transparent; font-size:.75rem; font-weight:700; }
      .slot-groups { display:grid; gap:.55rem; }
      .slot-group { padding:.65rem; border:1px solid #dbe1e4; border-radius:.45rem; background:#fff; }
      .slot-group strong { display:block; color:#33434C; font-size:.76rem; }
      .slot-group small { display:block; margin:.1rem 0 .45rem; color:#7b888e; font-size:.68rem; }
      .slot-list { display:flex; flex-wrap:wrap; gap:.42rem; }
      .slot { width:auto; padding:.48rem .62rem; border:1px solid #6b9bc2; border-radius:.3rem; color:#1f5a87; background:#f0f4f7; font-size:.74rem; }
      .slot:hover { background:#dfeaf1; }
      .slot.selected { color:#fff; background:${accent}; }
      .slots-empty { padding:.8rem; border:1px dashed #cbd3d7; border-radius:.4rem; color:#78868d; background:#fafbfb; font-size:.76rem; text-align:center; }
      .recurrence-card { display:grid; gap:.85rem; min-width:0; padding:1rem; border:1px solid #d1d5d7; border-radius:.45rem; background:#fff; }
      .recurrence-toggle { display:flex; align-items:flex-start; gap:.55rem; padding:.75rem; border:1px solid #b9cfe0; border-radius:.4rem; color:#33434C; background:#f0f4f7; font-size:.78rem; }
      .recurrence-toggle input, .weekday-option input, .occurrence-row input { flex:0 0 auto; margin-top:.18rem; accent-color:${accent}; }
      .recurrence-grid { display:grid; grid-template-columns:repeat(3,minmax(0,1fr)); gap:.65rem; min-width:0; }
      .recurrence-field { display:grid; gap:.35rem; color:#33434C; font-size:.74rem; font-weight:650; }
      .recurrence-field input, .recurrence-field select { width:100%; max-width:100%; min-width:0; padding:.65rem .7rem; border:1px solid #d1d5d7; border-radius:.38rem; color:#1A2C37; background:#fff; }
      .weekday-list { display:flex; flex-wrap:wrap; gap:.4rem; }
      .weekday-option { display:flex; align-items:center; gap:.28rem; padding:.4rem .48rem; border:1px solid #dbe1e4; border-radius:.35rem; color:#52636c; background:#fafbfb; font-size:.7rem; font-weight:600; }
      .recurrence-subfields { display:grid; gap:.65rem; padding-top:.2rem; }
      .recurrence-subfields[hidden], .recurrence-preview[hidden] { display:none; }
      .recurrence-preview { display:grid; gap:.55rem; margin-top:.25rem; padding-top:.9rem; border-top:1px solid #edf0f1; }
      .recurrence-preview h4 { margin:0; color:#33434C; font-size:.82rem; }
      .occurrence-list { display:grid; gap:.5rem; min-width:0; max-height:none; overflow:visible; }
       .occurrence-row { display:grid; grid-template-columns:auto minmax(0,1fr); gap:.5rem; min-width:0; padding:.65rem; border:1px solid #dbe1e4; border-radius:.4rem; background:#fff; }
       .occurrence-row.unavailable { border-color:#f0bcb6; background:#fff8f7; }
       .occurrence-row.removed { opacity:.78; border-style:dashed; background:#fafbfb; }
       .occurrence-toggle { display:flex; align-items:flex-start; gap:.3rem; color:#52636c; font-size:.64rem; font-weight:700; white-space:nowrap; }
      .occurrence-main { display:grid; gap:.35rem; min-width:0; }
      .occurrence-main strong { color:#33434C; font-size:.74rem; }
      .occurrence-main small { color:#6c7b82; font-size:.68rem; }
      .occurrence-controls { display:grid; grid-template-columns:repeat(2,minmax(0,1fr)); gap:.4rem; min-width:0; }
      .occurrence-field { display:grid; gap:.2rem; min-width:0; color:#6c7b82; font-size:.62rem; font-weight:650; }
      .occurrence-controls input, .occurrence-controls select { min-width:0; width:100%; max-width:100%; padding:.45rem; border:1px solid #d1d5d7; border-radius:.32rem; color:#33434C; background:#fff; font-size:.68rem; }
      .occurrence-actions { display:flex; align-items:center; justify-content:space-between; gap:.5rem; }
       .count-control { display:flex; align-items:center; gap:.35rem; }
       .count-control button { display:grid; place-items:center; width:1.8rem; height:1.8rem; padding:0; border:1px solid #6b9bc2; border-radius:.35rem; color:#075985; background:#e6f4fb; font-weight:800; }
       .count-control input { width:3.4rem; padding:.45rem; border:1px solid #d1d5d7; border-radius:.35rem; text-align:center; }
      .occurrence-adjustment { display:grid; gap:.18rem; padding:.5rem .6rem; border-left:3px solid #d97706; border-radius:.25rem; color:#7c4300; background:#fff7e8; font-size:.68rem; }
      .occurrence-adjustment strong { color:#7c4300; font-size:.69rem; }
      .occurrence-adjustment span, .occurrence-adjustment small { color:#8a5a18; font-size:.67rem; }
      .adjustment-confirm { display:flex; align-items:flex-start; gap:.35rem; color:#8a4b00; font-size:.68rem; font-weight:600; }
      .price-summary { display:flex; justify-content:space-between; gap:.75rem; min-width:0; padding:.65rem .75rem; border-radius:.4rem; color:#33434C; background:#f0f4f7; font-size:.75rem; }
       .price-summary strong { font-size:.88rem; }
       .price-updated { animation:kuroo-price-pulse 1.1s ease-out; }
       @keyframes kuroo-price-pulse { 0% { color:${accent}; transform:scale(1.05); } 100% { color:inherit; transform:scale(1); } }
       .price-with-tooltip { position:relative; display:inline-flex; align-items:center; max-width:100%; min-width:0; cursor:help; outline:none; }
       .price-tooltip { position:absolute; z-index:20; bottom:calc(100% + .45rem); left:50%; width:min(300px,calc(100vw - 2rem)); max-width:calc(100cqw - 1.5rem); max-height:240px; overflow:auto; overflow-wrap:anywhere; padding:.7rem .75rem; border:1px solid #b9cfe0; border-radius:.45rem; color:#33434C; background:#fff; box-shadow:0 10px 25px rgba(26,44,55,.18); font-size:.68rem; font-weight:500; opacity:0; pointer-events:none; transform:translate(-50%,.25rem); transition:opacity .14s ease,transform .14s ease; }
       .price-with-tooltip:hover .price-tooltip, .price-with-tooltip:focus .price-tooltip, .price-with-tooltip.open .price-tooltip { opacity:1; pointer-events:auto; transform:translate(-50%,0); }
       .price-tooltip strong { display:block; margin-bottom:.3rem; color:#1f5a87; font-size:.72rem; }
       .price-tooltip ul { display:grid; gap:.2rem; margin:.3rem 0; padding-left:1rem; }
       .price-tooltip footer { display:grid; gap:.16rem; padding-top:.35rem; border-top:1px solid #edf0f1; }
       .price-tooltip footer span { display:flex; justify-content:space-between; gap:.5rem; }
       .price-tooltip .positive { color:#8d2018; }
       .price-tooltip .negative { color:#16733a; }
       .recurrence-disabled-card { margin-top:.75rem; padding:.75rem; border:1px solid #e1e6e8; border-radius:.4rem; color:#66757d; background:#fafbfb; font-size:.72rem; }
      .recurring-information-summary { display:grid; gap:.55rem; margin-bottom:1rem; padding:.8rem; border:1px solid #b9cfe0; border-radius:.45rem; color:#33434C; background:#f0f4f7; }
      .recurring-information-summary[hidden] { display:none; }
      .recurring-information-summary header { display:flex; align-items:flex-start; justify-content:space-between; gap:.75rem; }
      .recurring-information-summary header strong { font-size:.78rem; }
      .recurring-information-summary header span { color:#1f5a87; font-size:.8rem; font-weight:800; white-space:nowrap; }
       .recurring-information-summary ul { display:grid; gap:.25rem; max-height:130px; margin:0; padding-left:1.1rem; color:#66757d; font-size:.7rem; }
       .recurring-information-summary button { width:max-content; padding:0; border:0; color:${accent}; background:transparent; font-size:.7rem; font-weight:750; }
       .review-card { display:grid; gap:.8rem; min-width:0; padding:1rem; border:1px solid #b9cfe0; border-radius:.45rem; background:#f7fbfd; }
       .review-card h4 { margin:0; color:#33434C; font-size:.9rem; }
       .review-lines { display:grid; gap:.4rem; margin:0; padding:0; list-style:none; color:#52636c; font-size:.72rem; }
       .review-lines li { display:flex; justify-content:space-between; gap:.7rem; }
       .review-lines li strong { color:#33434C; text-align:right; }
       .review-total { display:flex; align-items:center; justify-content:space-between; gap:.8rem; padding-top:.65rem; border-top:1px solid #dbe8ee; color:#33434C; }
       .review-total strong { font-size:1rem; }
       .review-coupon { display:grid; gap:.35rem; color:#33434C; font-size:.74rem; font-weight:700; }
       .review-coupon input { width:100%; padding:.65rem .7rem; border:1px solid #d1d5d7; border-radius:.38rem; background:#fff; }
       .review-note { margin:0; color:#66757d; font-size:.7rem; }
      .recurrence-hint { margin:0; color:#78868d; font-size:.7rem; }
      .participants { display:grid; gap:.45rem; }
      .participants h4 { margin:0; color:#33434C; font-size:.82rem; }
      .participant-row { display:grid; grid-template-columns:repeat(2,minmax(0,1fr)); gap:.4rem; }
      .participant-row input { min-width:0; padding:.55rem .6rem; border:1px solid #d1d5d7; border-radius:.35rem; }
      .secondary-button { width:max-content; padding:.5rem .7rem; border:1px solid #6b9bc2; border-radius:.35rem; color:#075985; background:#e6f4fb; font-size:.7rem; font-weight:700; }
      .hold { display:flex; align-items:center; justify-content:space-between; gap:.75rem; margin:0 0 1rem; padding:.7rem .8rem; border-radius:.4rem; color:#075985; background:#e6f4fb; font-size:.76rem; }
      .hold strong { font-size:.92rem; }
      .form-grid { display:grid; grid-template-columns:repeat(2,minmax(0,1fr)); gap:.75rem; min-width:0; }
      .form-grid .full { grid-column:1/-1; }
      .field textarea { min-height:5rem; resize:vertical; }
      .check-field { display:flex; grid-template-columns:none; align-items:flex-start; gap:.5rem; margin-top:.85rem; color:#52636c; font-size:.72rem; font-weight:500; }
      .check-field input { flex:0 0 auto; width:1rem; height:1rem; margin-top:.05rem; accent-color:${accent}; }
      .extras { display:grid; gap:.45rem; margin-top:1rem; padding-top:1rem; border-top:1px solid #edf0f1; }
      .extras h4 { margin:0; color:#33434C; font-size:.82rem; }
      .extra-option { display:flex; align-items:center; gap:.5rem; color:#52636c; font-size:.75rem; }
      .extra-option input { accent-color:${accent}; }
      .payment-card, .confirmation-card { display:grid; gap:.9rem; min-width:0; padding:1rem; border:1px solid #d1d5d7; border-radius:.45rem; background:#fff; }
      .payment-choice { display:flex; align-items:flex-start; gap:.65rem; padding:.75rem; border:1px solid #6b9bc2; border-radius:.4rem; background:#f0f4f7; }
      .payment-choice input { margin-top:.18rem; accent-color:${accent}; }
      .payment-choice strong, .payment-choice small { display:block; }
      .payment-choice strong { color:#33434C; font-size:.8rem; }
      .payment-choice small { margin-top:.15rem; color:#6c7b82; font-size:.7rem; }
      .payment-link { display:grid; gap:.65rem; padding:.8rem; border:1px solid #b9cfe0; border-radius:.4rem; color:#075985; background:#e6f4fb; font-size:.76rem; }
      .payment-link a { display:inline-block; width:max-content; max-width:100%; padding:.6rem .75rem; border-radius:.35rem; color:#fff; background:${accent}; font-weight:750; text-decoration:none; }
      .payment-link a:hover { filter:brightness(.94); }
      .payment-link.failed { border-color:#f0bcb6; color:#8d2018; background:#fff5f4; }
      .confirmation-card { justify-items:center; padding:1.5rem 1rem; text-align:center; }
      .confirmation-icon { display:grid; place-items:center; width:3rem; height:3rem; border-radius:50%; color:#fff; background:#019719; font-size:1.45rem; }
      .confirmation-card h3 { margin:0; }
      .confirmation-card p { margin:0; color:#66757d; font-size:.8rem; }
      .confirmation-detail { width:100%; padding:.7rem; border:1px solid #edf0f1; border-radius:.4rem; color:#52636c; background:#fafbfb; font-size:.75rem; }
      .booking-footer { display:flex; align-items:center; justify-content:space-between; gap:.8rem; min-height:76px; padding:1rem 1.65rem; border-top:1px solid #e2e6e8; box-shadow:0 -2px 5px rgba(26,44,55,.05); }
      .footer-back { padding:.65rem .8rem; border:0; color:#66757d; background:transparent; font-weight:650; }
      .footer-back[hidden] { display:none; }
      .primary-button { min-width:7.8rem; padding:.7rem 1rem; border:1px solid ${accent}; border-radius:.38rem; color:#fff; background:${accent}; font-weight:750; }
      .primary-button:hover:not(:disabled) { filter:brightness(.94); }
      .collapsed .sidebar { width:62px; padding:.8rem .6rem; }
      .collapsed { grid-template-columns:62px minmax(0,1fr); }
      .collapsed .brand-copy, .collapsed .step-copy, .collapsed .sidebar-contact, .collapsed .collapse-label { display:none; }
      .collapsed .brand, .collapsed .step-button { justify-content:center; }
      .collapsed .collapse-button { justify-content:center; }
      @container (max-width:720px) {
        .booking-shell, .collapsed { grid-template-columns:1fr; height:auto; min-height:0; overflow:visible; border-radius:0 0 10px 10px; }
        .sidebar, .collapsed .sidebar { width:auto; min-width:0; padding:.75rem; }
        .brand { padding:.15rem .25rem .65rem; }
        .brand-copy, .collapsed .brand-copy { display:inline; }
        .steps { display:grid; grid-template-columns:repeat(4,minmax(0,1fr)); gap:.3rem; overflow:hidden; }
        .step { min-width:0; }
        .step-button { min-width:0; flex-direction:column; align-items:center; justify-content:center; gap:.2rem; padding:.5rem .25rem; text-align:center; }
        .step-copy, .collapsed .step-copy { display:grid; width:100%; text-align:center; }
        .step-copy strong, .step-copy small { overflow:visible; text-overflow:clip; white-space:normal; line-height:1.15; }
        .step-copy strong { font-size:.68rem; }
        .step-copy small { font-size:.59rem; }
        .sidebar-contact, .collapse-button { display:none; }
        .booking-main { height:auto; min-height:0; }
        .booking-body { overflow:visible; padding:1rem; }
        .booking-header, .booking-footer { padding-left:1rem; padding-right:1rem; }
        .booking-footer { flex-wrap:wrap; min-height:0; }
        .footer-back, .primary-button { flex:1 1 0; min-width:0; }
        .filter-grid, .form-grid, .recurrence-grid { grid-template-columns:1fr; }
        .filter-field:first-child, .form-grid .full { grid-column:auto; }
        .participant-row { grid-template-columns:1fr; }
        .occurrence-list { max-height:none; overflow:visible; }
        .occurrence-row { grid-template-columns:1fr; gap:.7rem; }
        .occurrence-actions { flex-wrap:wrap; }
        .occurrence-controls { grid-template-columns:repeat(2,minmax(0,1fr)); }
        .price-summary { align-items:flex-start; flex-direction:column; }
        .price-with-tooltip { display:block; width:100%; }
        .price-tooltip { position:static; display:block; width:100%; max-width:100%; max-height:0; margin:0; padding:0; border:0; box-shadow:none; opacity:0; pointer-events:none; transform:none; overflow:hidden; }
        .price-with-tooltip:hover .price-tooltip, .price-with-tooltip:focus .price-tooltip, .price-with-tooltip.open .price-tooltip { max-height:18rem; margin-top:.4rem; padding:.65rem .7rem; border:1px solid #b9cfe0; opacity:1; pointer-events:auto; transform:none; }
        .price-tooltip footer span { align-items:flex-start; flex-wrap:wrap; }
        .review-lines li { align-items:flex-start; flex-direction:column; gap:.15rem; }
        .review-lines li strong { text-align:left; }
      }
      @container (max-width:480px) {
        .booking-body { padding:.75rem; }
        .booking-header { padding-top:.8rem; padding-bottom:.8rem; }
        .booking-header h2 { font-size:1.05rem; }
        .step-button { padding:.4rem .15rem; }
        .step-copy small { display:none; }
        .step-copy strong { font-size:.64rem; }
        .calendar-toolbar { grid-template-columns:minmax(0,1fr) minmax(0,1fr) auto auto; gap:.35rem; }
        .calendar-toolbar select { padding:.58rem .4rem; font-size:.76rem; }
        .calendar-week, .calendar-grid { gap:.25rem; }
        .calendar-day { min-height:2.3rem; font-size:.78rem; }
        .occurrence-controls { grid-template-columns:1fr; }
        .occurrence-actions .secondary-button { width:100%; }
        .booking-footer { align-items:stretch; flex-direction:column; }
        .footer-back, .primary-button { width:100%; flex-basis:auto; }
      }
      :host([data-layout="compact"]) .booking-shell,
      :host([data-layout="narrow"]) .booking-shell,
      :host([data-layout="compact"]) .collapsed,
      :host([data-layout="narrow"]) .collapsed { grid-template-columns:1fr; height:auto; min-height:0; overflow:visible; border-radius:0 0 10px 10px; }
      :host([data-layout="compact"]) .sidebar,
      :host([data-layout="narrow"]) .sidebar,
      :host([data-layout="compact"]) .collapsed .sidebar,
      :host([data-layout="narrow"]) .collapsed .sidebar { width:auto; min-width:0; padding:.75rem; }
      :host([data-layout="compact"]) .brand,
      :host([data-layout="narrow"]) .brand { padding:.15rem .25rem .65rem; }
      :host([data-layout="compact"]) .brand-copy,
      :host([data-layout="narrow"]) .brand-copy,
      :host([data-layout="compact"]) .collapsed .brand-copy,
      :host([data-layout="narrow"]) .collapsed .brand-copy { display:inline; }
      :host([data-layout="compact"]) .steps,
      :host([data-layout="narrow"]) .steps { display:grid; grid-template-columns:repeat(4,minmax(0,1fr)); gap:.3rem; overflow:hidden; }
      :host([data-layout="compact"]) .step,
      :host([data-layout="narrow"]) .step { min-width:0; }
      :host([data-layout="compact"]) .step-button,
      :host([data-layout="narrow"]) .step-button { min-width:0; flex-direction:column; align-items:center; justify-content:center; gap:.2rem; padding:.5rem .25rem; text-align:center; }
      :host([data-layout="compact"]) .step-copy,
      :host([data-layout="narrow"]) .step-copy,
      :host([data-layout="compact"]) .collapsed .step-copy,
      :host([data-layout="narrow"]) .collapsed .step-copy { display:grid; width:100%; text-align:center; }
      :host([data-layout="compact"]) .step-copy strong,
      :host([data-layout="compact"]) .step-copy small,
      :host([data-layout="narrow"]) .step-copy strong,
      :host([data-layout="narrow"]) .step-copy small { overflow:visible; text-overflow:clip; white-space:normal; line-height:1.15; }
      :host([data-layout="compact"]) .step-copy strong,
      :host([data-layout="narrow"]) .step-copy strong { font-size:.68rem; }
      :host([data-layout="compact"]) .step-copy small,
      :host([data-layout="narrow"]) .step-copy small { font-size:.59rem; }
      :host([data-layout="compact"]) .sidebar-contact,
      :host([data-layout="compact"]) .collapse-button,
      :host([data-layout="narrow"]) .sidebar-contact,
      :host([data-layout="narrow"]) .collapse-button { display:none; }
      :host([data-layout="compact"]) .booking-main,
      :host([data-layout="narrow"]) .booking-main { height:auto; min-height:0; }
      :host([data-layout="compact"]) .booking-body,
      :host([data-layout="narrow"]) .booking-body { overflow:visible; padding:1rem; }
      :host([data-layout="compact"]) .booking-footer,
      :host([data-layout="narrow"]) .booking-footer { flex-wrap:wrap; min-height:0; padding-left:1rem; padding-right:1rem; }
      :host([data-layout="compact"]) .footer-back,
      :host([data-layout="compact"]) .primary-button,
      :host([data-layout="narrow"]) .footer-back,
      :host([data-layout="narrow"]) .primary-button { flex:1 1 0; min-width:0; }
      :host([data-layout="compact"]) .filter-grid,
      :host([data-layout="compact"]) .form-grid,
      :host([data-layout="compact"]) .recurrence-grid,
      :host([data-layout="narrow"]) .filter-grid,
      :host([data-layout="narrow"]) .form-grid,
      :host([data-layout="narrow"]) .recurrence-grid { grid-template-columns:1fr; }
      :host([data-layout="compact"]) .filter-field:first-child,
      :host([data-layout="compact"]) .form-grid .full,
      :host([data-layout="narrow"]) .filter-field:first-child,
      :host([data-layout="narrow"]) .form-grid .full { grid-column:auto; }
      :host([data-layout="compact"]) .participant-row,
      :host([data-layout="narrow"]) .participant-row { grid-template-columns:1fr; }
      :host([data-layout="compact"]) .occurrence-list,
      :host([data-layout="narrow"]) .occurrence-list { max-height:none; overflow:visible; }
      :host([data-layout="compact"]) .occurrence-row,
      :host([data-layout="narrow"]) .occurrence-row { grid-template-columns:1fr; gap:.7rem; }
      :host([data-layout="compact"]) .occurrence-actions,
      :host([data-layout="narrow"]) .occurrence-actions { flex-wrap:wrap; }
      :host([data-layout="compact"]) .occurrence-controls,
      :host([data-layout="narrow"]) .occurrence-controls { grid-template-columns:repeat(2,minmax(0,1fr)); }
      :host([data-layout="compact"]) .price-summary,
      :host([data-layout="narrow"]) .price-summary { align-items:flex-start; flex-direction:column; }
      :host([data-layout="compact"]) .price-with-tooltip,
      :host([data-layout="narrow"]) .price-with-tooltip { display:block; width:100%; }
      :host([data-layout="compact"]) .price-tooltip,
      :host([data-layout="narrow"]) .price-tooltip { position:static; display:block; width:100%; max-width:100%; max-height:0; margin:0; padding:0; border:0; box-shadow:none; opacity:0; pointer-events:none; transform:none; overflow:hidden; }
      :host([data-layout="compact"]) .price-with-tooltip:hover .price-tooltip,
      :host([data-layout="compact"]) .price-with-tooltip:focus .price-tooltip,
      :host([data-layout="compact"]) .price-with-tooltip.open .price-tooltip,
      :host([data-layout="narrow"]) .price-with-tooltip:hover .price-tooltip,
      :host([data-layout="narrow"]) .price-with-tooltip:focus .price-tooltip,
      :host([data-layout="narrow"]) .price-with-tooltip.open .price-tooltip { max-height:18rem; margin-top:.4rem; padding:.65rem .7rem; border:1px solid #b9cfe0; opacity:1; pointer-events:auto; transform:none; }
      :host([data-layout="compact"]) .price-tooltip footer span,
      :host([data-layout="narrow"]) .price-tooltip footer span { align-items:flex-start; flex-wrap:wrap; }
      :host([data-layout="compact"]) .review-lines li,
      :host([data-layout="narrow"]) .review-lines li { align-items:flex-start; flex-direction:column; gap:.15rem; }
      :host([data-layout="compact"]) .review-lines li strong,
      :host([data-layout="narrow"]) .review-lines li strong { text-align:left; }
    </style>
    <form class="booking-shell" novalidate>
      <aside class="sidebar" aria-label="Progreso de la reserva">
        <div class="brand"><span class="brand-mark" aria-hidden="true">◫</span><span class="brand-copy">${escapeHtml(PRODUCT_NAME)}</span></div>
        <ol class="steps">${bookingSteps.map((step, index) => `<li class="step" data-step="${step.key}"><button type="button" class="step-button" data-step-target="${step.key}"><span class="step-index" data-step-index>${index + 1}</span><span class="step-copy"><strong>${escapeHtml(step.label)}</strong><small>${step.key === "datetime" ? "Servicio y horario" : step.key === "recurrence" ? "Definí las fechas" : step.key === "information" ? "Completá tus datos" : "Método de pago"}</small></span></button></li>`).join("")}</ol>
        <div class="sidebar-contact"><strong>¿Necesitás ayuda?</strong><small>Estamos para acompañarte en tu reserva.</small></div>
        <button type="button" class="collapse-button" data-action="collapse"><span class="collapse-label">Contraer menú</span><span class="collapse-icon" aria-hidden="true">‹</span></button>
      </aside>
      <section class="booking-main">
        <header class="booking-header"><button type="button" class="back-button" data-action="back" aria-label="Volver">‹</button><div class="header-copy"><p class="header-eyebrow">${escapeHtml(PRODUCT_NAME)}</p><h2 data-step-title>${escapeHtml(stepTitles[this.currentStep])}</h2></div></header>
        <div class="booking-body">
          <div class="status" role="status" aria-live="polite"></div>
          <section class="step-panel" data-step-panel="datetime">
            <h3>Elegí tu servicio, fecha y horario</h3>
            <p class="step-intro">Seleccioná el servicio y los filtros para consultar los horarios disponibles en tiempo real.</p>
             <div class="filter-grid"><label class="filter-field service-filter">Servicio<select name="serviceId" data-service-inline aria-label="Servicio" ${serviceId ? "disabled" : ""} required><option value="">Cargando servicios…</option></select></label><label class="filter-field">Profesional<select name="providerId"><option value="">Todos los profesionales</option></select></label><label class="filter-field">Sede<select name="locationId"><option value="">Todas las sedes</option></select></label></div>
            <div class="service-choice" data-service-choice hidden><div class="service-meta" data-service-meta hidden><span data-service-description></span><span class="service-payment-summary" data-service-payment></span><strong data-service-price></strong></div><div class="catalog-retry" data-catalog-retry hidden><button type="button" data-retry data-retry-catalog>Reintentar</button></div></div>
            <div class="calendar-card"><div class="calendar-toolbar"><select data-calendar-month aria-label="Mes"><option value="${String(defaultMonth.getMonth())}">${escapeHtml(monthLabel.split(" ")[0] ?? monthLabel)}</option></select><select data-calendar-year aria-label="Año"><option value="${defaultMonth.getFullYear()}">${defaultMonth.getFullYear()}</option></select><button type="button" class="month-button" data-action="prev-month" aria-label="Mes anterior">‹</button><button type="button" class="month-button" data-action="next-month" aria-label="Mes siguiente">›</button></div><div class="calendar-week" aria-hidden="true">${weekdayLabels.map((day) => `<span>${day}</span>`).join("")}</div><div class="calendar-grid" data-calendar-grid aria-label="Calendario"></div><p class="calendar-note">Elegí un día resaltado para consultar sus horarios.</p></div>
            <input name="date" type="hidden" value="${this.selectedDate}" />
            <div class="slots-heading"><strong>Horarios disponibles</strong><button type="button" class="next-button" data-action="next-date">Ver próximo horario</button></div>
            <div class="slot-groups" data-slots aria-live="polite"><div class="slots-empty">Seleccioná una fecha para consultar horarios.</div></div>
             <label class="recurrence-toggle"><input name="recurringEnabled" type="checkbox" disabled /> <span><strong>Repetir esta reserva</strong><small>Programá varias citas con este servicio. Podés revisar y modificar cada fecha y horario antes de confirmar.</small><small data-recurrence-availability>Este servicio no tiene recurrencias habilitadas.</small></span></label><div class="recurrence-disabled-card" data-recurrence-disabled-card hidden>Este servicio no tiene recurrencias habilitadas. Podés reservar un único turno desde esta sección.</div>
          </section>
          <section class="step-panel" data-step-panel="recurrence" hidden>
            <h3>¿Cómo querés repetirla?</h3>
            <p class="step-intro">Calculamos cada fecha en el servidor y te mostramos cualquier ajuste antes de confirmar.</p>
            <div class="recurrence-card">
              <div class="recurrence-grid"><label class="recurrence-field">Frecuencia<select name="recurrenceFrequency"><option value="daily">Diaria</option><option value="weekly" selected>Semanal</option><option value="monthly">Mensual</option></select></label><label class="recurrence-field">Cada<input name="recurrenceInterval" type="number" min="1" max="52" value="1" required /><small>intervalo</small></label><label class="recurrence-field">Ocurrencias<div class="count-control"><button type="button" data-action="recurrence-decrease" aria-label="Disminuir ocurrencias">−</button><input name="recurrenceCount" type="number" min="1" max="52" value="4" required aria-label="Cantidad de ocurrencias" /><button type="button" data-action="recurrence-increase" aria-label="Aumentar ocurrencias">+</button></div></label></div>
              <div class="recurrence-subfields" data-weekly-fields><strong>Repetir los días</strong><div class="weekday-list">${[1, 2, 3, 4, 5, 6, 0].map((day) => `<label class="weekday-option"><input type="checkbox" name="weeklyDay" value="${day}"${day === 1 ? " checked" : ""} />${["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"][day]}</label>`).join("")}</div></div>
              <div class="recurrence-subfields" data-monthly-fields hidden><label class="recurrence-field">Modalidad mensual<select name="monthlyMode"><option value="day_of_month">Día del mes</option><option value="weekday_position">Posición del día de la semana</option></select></label><div data-monthly-day-field><label class="recurrence-field">Día<input name="monthlyDay" type="number" min="1" max="31" value="1" /></label></div><div class="recurrence-grid" data-monthly-position-fields hidden><label class="recurrence-field">Día<select name="monthlyWeekday">${["Domingo", "Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado"].map((day, index) => `<option value="${index}">${day}</option>`).join("")}</select></label><label class="recurrence-field">Posición<select name="monthlyWeek"><option value="1">Primero</option><option value="2">Segundo</option><option value="3">Tercero</option><option value="4">Cuarto</option><option value="-1">Último</option></select></label></div></div>
               <div class="participants" data-recurring-participants><h4>Participantes para toda la serie</h4><div data-recurring-participant-list><div class="participant-row" data-recurring-participant><input name="recurringParticipantName" placeholder="Nombre" aria-label="Nombre del participante" /><input name="recurringParticipantEmail" type="email" placeholder="Email (opcional)" aria-label="Email del participante" /></div></div><button type="button" class="secondary-button" data-action="add-recurring-participant">+ Agregar participante</button></div>
              <p class="recurrence-hint">Las fechas y horas se proponen con el mismo horario del turno original. Podés editarlas; cada cambio vuelve a verificar disponibilidad y precio.</p>
              <div class="recurrence-preview" data-recurring-preview hidden></div>
            </div>
          </section>
          <section class="step-panel" data-step-panel="information" hidden>
            <h3>Contanos quién sos</h3>
            <p class="step-intro">Estos datos se usarán únicamente para gestionar tu reserva.</p>
             <div data-customer hidden><div class="recurring-information-summary" data-recurring-information-summary hidden></div><div class="hold" data-hold hidden><span>Horario reservado temporalmente</span><strong data-countdown>10:00</strong></div><div class="form-grid"><label class="field">Nombre<input name="firstName" autocomplete="given-name" required /></label><label class="field">Apellido<input name="lastName" autocomplete="family-name" /></label><label class="field">Email<input name="email" type="email" autocomplete="email" required /></label><label class="field">Teléfono<input name="phone" type="tel" autocomplete="tel" /></label><label class="field">Personas<input name="persons" type="number" min="1" max="52" value="1" required /></label><label class="field full">Notas<textarea name="notes" rows="2" placeholder="Información adicional (opcional)"></textarea></label></div><label class="check-field"><input name="consent" type="checkbox" required /> <span>Acepto que mis datos se utilicen para gestionar la reserva.</span></label><div class="extras" data-extras hidden><h4>Extras</h4><div data-extra-list></div></div><div class="extras" data-recurring-extras hidden><h4>Extras para toda la serie</h4><div data-recurring-extra-list></div></div></div><div class="slots-empty" data-customer-empty>Primero elegí un horario para continuar.</div>
          </section>
          <section class="step-panel" data-step-panel="payment" hidden>
            <h3>Revisión y pago</h3>
            <p class="step-intro">Revisá fechas, importes y cupón antes de crear el hold o el checkout.</p>
            <div class="payment-card" data-payment-content><p class="step-intro" style="margin:0">Preparando el estado del pago…</p></div>
          </section>
          <section class="step-panel" data-step-panel="confirmation" hidden>
            <div class="confirmation-card"><div class="confirmation-icon" aria-hidden="true">✓</div><h3>¡Listo! Tu reserva fue confirmada</h3><p>Guardá el código de confirmación para futuras consultas.</p><div class="confirmation-detail" data-confirmation-detail></div></div>
          </section>
        </div>
        <footer class="booking-footer"><button type="button" class="footer-back" data-action="previous">Volver</button><button type="submit" class="primary-button" data-action="continue" disabled>Continuar</button></footer>
      </section>
    </form>`;
    this.bindForm();
    this.renderCalendar();
    this.updateStepUI();
  }

  private bindForm(): void {
    const form = this.root.querySelector("form") as HTMLFormElement | null;
    if (!form) return;
    const dateInput = form.elements.namedItem("date") as HTMLInputElement;
    dateInput.value = this.selectedDate;
    const serviceSelect = form.elements.namedItem("serviceId") as HTMLSelectElement;
    serviceSelect?.addEventListener("change", () => {
      this.weeklyDaysCustomized = false;
      void this.handleServiceChange(form, serviceSelect.value);
    });
    for (const name of ["providerId", "locationId"]) {
      const control = form.elements.namedItem(name) as HTMLSelectElement;
      control?.addEventListener("change", () => {
        if (name === "providerId") {
          const locationSelect = form.elements.namedItem("locationId") as HTMLSelectElement | null;
          if (locationSelect && !this.getAttribute("location-id")) locationSelect.value = "";
          void this.loadCatalogAndSlots();
          return;
        }
        void this.loadSlots();
      });
    }
    const recurrenceToggle = form.elements.namedItem("recurringEnabled") as HTMLInputElement | null;
    recurrenceToggle?.addEventListener("change", () => {
      this.cancelRecurringPreviewRequest({ clearPreview: true });
      this.renderExtras(form);
      this.updateStepUI();
    });
    form.querySelectorAll<HTMLInputElement>('input[name="weeklyDay"]').forEach((input) => input.addEventListener("change", () => { this.weeklyDaysCustomized = true; this.scheduleRecurringPreviewRefresh(form); }));
    this.root.querySelector('[data-action="add-recurring-participant"]')?.addEventListener("click", () => this.addRecurringParticipant(form));
    form.querySelectorAll<HTMLInputElement>('[data-recurring-participant] input').forEach((input) => input.addEventListener("input", () => this.scheduleRecurringPreviewRefresh(form)));
    const recurrenceFrequency = form.elements.namedItem("recurrenceFrequency") as HTMLSelectElement | null;
    recurrenceFrequency?.addEventListener("change", () => { this.updateRecurrenceFields(form); this.scheduleRecurringPreviewRefresh(form); });
    const monthlyMode = form.elements.namedItem("monthlyMode") as HTMLSelectElement | null;
    monthlyMode?.addEventListener("change", () => { this.updateRecurrenceFields(form); this.scheduleRecurringPreviewRefresh(form); });
    for (const name of ["recurrenceInterval", "recurrenceCount", "monthlyDay", "monthlyWeekday", "monthlyWeek", "persons"]) {
      const control = form.elements.namedItem(name) as HTMLInputElement | HTMLSelectElement | null;
      control?.addEventListener("input", () => this.scheduleRecurringPreviewRefresh(form));
      control?.addEventListener("change", () => this.scheduleRecurringPreviewRefresh(form));
    }
    const personsControl = form.elements.namedItem("persons") as HTMLInputElement | null;
    personsControl?.addEventListener("input", () => {
      this.oneOffQuote = undefined;
      if (!recurrenceToggle?.checked && this.currentStep === "payment") this.scheduleReviewQuoteRefresh(form);
    });
    personsControl?.addEventListener("change", () => {
      this.oneOffQuote = undefined;
      if (!recurrenceToggle?.checked && this.currentStep === "payment") this.scheduleReviewQuoteRefresh(form);
    });
    form.addEventListener("input", (event) => {
      const target = event.target as HTMLElement | null;
      if (target?.matches("[data-review-coupon]")) {
        this.scheduleReviewQuoteRefresh(form);
      }
    });
    form.addEventListener("change", (event) => {
      const target = event.target as HTMLElement | null;
      if (target?.matches("[data-review-coupon]")) this.scheduleReviewQuoteRefresh(form);
    });
    form.querySelectorAll<HTMLInputElement>('input[name^="recurring-extra-"]').forEach((input) => input.addEventListener("change", () => this.scheduleRecurringPreviewRefresh(form)));
    this.root.querySelector('[data-action="prev-month"]')?.addEventListener("click", () => this.changeMonth(-1));
    this.root.querySelector('[data-action="next-month"]')?.addEventListener("click", () => this.changeMonth(1));
    this.root.querySelector('[data-action="next-date"]')?.addEventListener("click", () => void this.findNextDate());
    this.root.querySelector('[data-action="recurrence-decrease"]')?.addEventListener("click", () => this.changeRecurrenceCount(form, -1));
    this.root.querySelector('[data-action="recurrence-increase"]')?.addEventListener("click", () => this.changeRecurrenceCount(form, 1));
    this.root.querySelector('[data-retry-catalog]')?.addEventListener("click", () => {
      const retry = this.root.querySelector("[data-catalog-retry]") as HTMLElement | null;
      if (retry) retry.hidden = true;
      void this.loadCatalogAndSlots();
    });
    this.root.querySelector('[data-action="reset"]')?.addEventListener("click", () => { void this.resetBooking(); });
    this.root.querySelector('[data-action="back"]')?.addEventListener("click", () => void this.goPrevious());
    this.root.querySelector('[data-action="previous"]')?.addEventListener("click", () => void this.goPrevious());
    this.root.querySelector('[data-action="collapse"]')?.addEventListener("click", () => {
      const shell = this.root.querySelector(".booking-shell");
      const label = this.root.querySelector(".collapse-label");
      shell?.classList.toggle("collapsed");
      if (label) label.textContent = shell?.classList.contains("collapsed") ? "Expandir menú" : "Contraer menú";
    });
    this.root.querySelectorAll("[data-step-target]").forEach((node) => node.addEventListener("click", () => {
      const target = (node as HTMLElement).dataset.stepTarget as BookingStep | undefined;
      if (target) void this.goToStep(target);
    }));
    this.updateRecurrenceAvailability(form);
    this.updateRecurrenceFields(form);
    form.addEventListener("submit", (event) => void this.submit(event as SubmitEvent));
  }

  private updateStepUI(): void {
    const currentIndex = bookingSteps.findIndex((step) => step.key === this.currentStep);
    this.root.querySelectorAll<HTMLElement>("[data-step-panel]").forEach((panel) => { panel.hidden = panel.dataset.stepPanel !== this.currentStep; });
    this.root.querySelectorAll<HTMLElement>("[data-step]").forEach((step) => {
      const index = bookingSteps.findIndex((item) => item.key === step.dataset.step);
      step.classList.toggle("active", index === currentIndex);
      step.classList.toggle("complete", index >= 0 && index < currentIndex || this.currentStep === "confirmation");
      const marker = step.querySelector("[data-step-index]");
      if (marker) marker.textContent = index >= 0 && index < currentIndex || this.currentStep === "confirmation" ? "✓" : String(index + 1);
      const button = step.querySelector("button");
      if (button) button.disabled = !(index >= 0 && index <= currentIndex) || this.currentStep === "confirmation";
    });
    const title = this.root.querySelector("[data-step-title]");
    if (title) title.textContent = stepTitles[this.currentStep];
    const submit = this.root.querySelector('[data-action="continue"]') as HTMLButtonElement | null;
    if (submit) {
      submit.textContent = this.continueLabel();
      submit.disabled = this.continueDisabled();
    }
    const form = this.root.querySelector("form") as HTMLFormElement | null;
    const busy = this.availabilityLoading || this.recurringPricingLoading || this.oneOffQuoteLoading || this.paymentRefreshing || this.reviewRefreshPending;
    if (form) form.setAttribute("aria-busy", String(busy));
    const body = this.root.querySelector(".booking-body") as HTMLElement | null;
    if (body) body.setAttribute("aria-busy", String(busy));
    const back = this.root.querySelector('[data-action="back"]') as HTMLButtonElement | null;
    const previous = this.root.querySelector('[data-action="previous"]') as HTMLButtonElement | null;
    const canGoBack = this.currentStep !== "datetime" && this.currentStep !== "confirmation";
    if (back) back.disabled = !canGoBack;
    if (previous) previous.hidden = !canGoBack;
    if (this.currentStep === "information") {
      const customer = this.root.querySelector("[data-customer]") as HTMLElement | null;
      const empty = this.root.querySelector("[data-customer-empty]") as HTMLElement | null;
      if (customer) customer.hidden = !this.selectedSlot && !this.pendingHold && !this.recurringPreview && !this.bookingCheckout;
      if (empty) empty.hidden = Boolean(this.selectedSlot || this.pendingHold || this.recurringPreview || this.bookingCheckout);
    }
    this.renderRecurringInformationSummary();
    if (this.currentStep === "recurrence") this.renderRecurringPreview();
    if (this.currentStep === "payment") this.renderPayment();
    if (this.currentStep === "confirmation") this.renderConfirmation();
  }

  private continueLabel(): string {
    if (this.currentStep === "confirmation") return this.lastBooking ? "Reserva confirmada" : "Nueva reserva";
    if (this.currentStep === "information" && this.pendingHold) return "Confirmar reserva";
    if (this.currentStep === "information" && this.recurringPreview) return "Ir a revisión y pago";
    if (this.currentStep === "information" && !this.pendingHold) return "Ir a revisión y pago";
    if (this.currentStep === "payment" && !this.recurringCheckout && !this.bookingCheckout) return this.recurringPreview ? "Confirmar y crear checkout" : "Confirmar reserva";
    if (this.currentStep === "payment") return "Ver estado del pago";
    if (this.currentStep === "recurrence") return this.recurringPreview ? "Continuar con estas fechas" : "Previsualizar fechas";
    if (this.currentStep === "datetime" && !this.serviceIdFromForm()) return "Seleccionar servicio";
    if (this.currentStep === "datetime" && !this.selectedSlot) return "Comprobar disponibilidad";
    return "Continuar";
  }

  private continueDisabled(): boolean {
    if (this.availabilityLoading || this.recurringPricingLoading || this.oneOffQuoteLoading || this.paymentRefreshing || this.reviewRefreshPending) return true;
    if (this.currentStep === "confirmation") return Boolean(this.lastBooking);
    if (this.currentStep === "datetime") return !this.serviceIdFromForm() || !this.selectedSlot;
    if (this.currentStep === "recurrence") {
      if (!this.selectedSlot) return true;
      if (!this.recurringPreview) return false;
      const selections = this.selectedRecurringOccurrences();
      return !selections.some((occurrence) => occurrence.include) || this.recurringPreview.occurrences.some((occurrence) => occurrence.status === "unavailable" && selections.some((selection) => selection.sequence === occurrence.sequence && selection.include)) || this.recurringPreview.occurrences.some((occurrence) => occurrence.adjusted && selections.some((selection) => selection.sequence === occurrence.sequence && selection.include && !selection.adjustmentAccepted));
    }
    if (this.currentStep === "information") return !this.selectedSlot || (Boolean(this.recurringPreview) && !this.selectedRecurringOccurrences().some((occurrence) => occurrence.include));
    if (this.currentStep === "payment") return !this.recurringCheckout && !this.bookingCheckout && !this.selectedSlot;
    return false;
  }

  private serviceIdFromForm(): string {
    const form = this.root.querySelector("form") as HTMLFormElement | null;
    return this.getAttribute("service-id") ?? (form?.elements.namedItem("serviceId") as HTMLSelectElement | null)?.value ?? "";
  }

  private serviceRecurrence(): ServiceSummary["recurrence"] {
    return this.services.find((service) => service.id === this.serviceIdFromForm())?.recurrence;
  }

  private servicePaymentMode(): ServicePaymentMode {
    const service = this.services.find((item) => item.id === this.serviceIdFromForm());
    return service?.paymentMode ?? service?.recurrence?.paymentMode ?? "on_site";
  }

  private updateRecurrenceAvailability(form: HTMLFormElement): void {
    const toggle = form.elements.namedItem("recurringEnabled") as HTMLInputElement | null;
    const note = form.querySelector("[data-recurrence-availability]") as HTMLElement | null;
    const disabledCard = form.querySelector("[data-recurrence-disabled-card]") as HTMLElement | null;
    const settings = this.serviceRecurrence();
    if (!toggle) return;
    toggle.disabled = !settings?.enabled;
    if (!settings?.enabled) {
      toggle.checked = false;
      this.recurringPreview = undefined;
      if (note) note.textContent = "Este servicio no tiene recurrencias habilitadas.";
      if (disabledCard) disabledCard.hidden = false;
      return;
    }
    if (disabledCard) disabledCard.hidden = true;
    if (note) {
      const allowedFrequencies = [["daily", settings.allowDaily, "diaria"], ["weekly", settings.allowWeekly, "semanal"], ["monthly", settings.allowMonthly, "mensual"]].filter(([, enabled]) => enabled).map(([, , label]) => label).join(", ");
      const cancellationWindow = settings.cancellationWindowMinutes / 60 >= 24 ? String(Math.round(settings.cancellationWindowMinutes / 1440)) + " día(s)" : String(settings.cancellationWindowMinutes) + " min";
      note.textContent = (allowedFrequencies || "Ninguna") + " · " + cancellationWindow + " para cancelar.";
    }
    const frequency = form.elements.namedItem("recurrenceFrequency") as HTMLSelectElement | null;
    if (frequency) {
      const allowed: Record<string, boolean> = { daily: settings.allowDaily, weekly: settings.allowWeekly, monthly: settings.allowMonthly };
      [...frequency.options].forEach((option) => { option.disabled = !allowed[option.value]; });
      if (frequency.selectedOptions[0]?.disabled) {
        const first = [...frequency.options].find((option) => !option.disabled);
        if (first) frequency.value = first.value;
      }
    }
  }

  private updateRecurrenceFields(form: HTMLFormElement): void {
    const frequency = (form.elements.namedItem("recurrenceFrequency") as HTMLSelectElement | null)?.value;
    const weekly = form.querySelector("[data-weekly-fields]") as HTMLElement | null;
    const monthly = form.querySelector("[data-monthly-fields]") as HTMLElement | null;
    if (weekly) weekly.hidden = frequency !== "weekly";
    if (monthly) monthly.hidden = frequency !== "monthly";
    const mode = (form.elements.namedItem("monthlyMode") as HTMLSelectElement | null)?.value;
    const dayField = form.querySelector("[data-monthly-day-field]") as HTMLElement | null;
    const positionFields = form.querySelector("[data-monthly-position-fields]") as HTMLElement | null;
    if (dayField) dayField.hidden = mode !== "day_of_month";
    if (positionFields) positionFields.hidden = mode !== "weekday_position";
  }

  private recurringRuleFromForm(form: HTMLFormElement): RecurringRule {
    const frequency = (form.elements.namedItem("recurrenceFrequency") as HTMLSelectElement | null)?.value as RecurringFrequency | undefined;
    const interval = Math.max(1, Number((form.elements.namedItem("recurrenceInterval") as HTMLInputElement | null)?.value || 1));
    if (!frequency || !Number.isInteger(interval) || interval > 52) throw new Error("Elegí una frecuencia e intervalo válidos.");
    const rule: RecurringRule = { frequency, interval };
    if (frequency === "weekly") {
      const weeklyDays = [...form.querySelectorAll<HTMLInputElement>('input[name="weeklyDay"]:checked')].map((input) => Number(input.value));
      if (!weeklyDays.length) throw new Error("Seleccioná al menos un día de la semana.");
      rule.weeklyDays = weeklyDays;
    }
    if (frequency === "monthly") {
      const monthlyMode = (form.elements.namedItem("monthlyMode") as HTMLSelectElement | null)?.value === "weekday_position" ? "weekday_position" : "day_of_month";
      rule.monthlyMode = monthlyMode;
      if (monthlyMode === "day_of_month") {
        const monthlyDay = Number((form.elements.namedItem("monthlyDay") as HTMLInputElement | null)?.value || 1);
        if (!Number.isInteger(monthlyDay) || monthlyDay < 1 || monthlyDay > 31) throw new Error("El día mensual debe estar entre 1 y 31.");
        rule.monthlyDay = monthlyDay;
      } else {
        rule.monthlyWeekday = Number((form.elements.namedItem("monthlyWeekday") as HTMLSelectElement | null)?.value || 0);
        rule.monthlyWeek = Number((form.elements.namedItem("monthlyWeek") as HTMLSelectElement | null)?.value || 1);
      }
    }
    return rule;
  }

  private selectedExtrasFromForm(form: HTMLFormElement, recurring = false): BookingExtraSelection[] {
    const prefix = recurring ? "recurring-extra-" : "extra-";
    return this.extras.filter((extra) => {
      const input = form.querySelector<HTMLInputElement>(`input[name="${prefix}${CSS.escape(extra.id)}"]`);
      return Boolean(input?.checked);
    }).map((extra) => ({ extraId: extra.id, quantity: 1 }));
  }

  private renderRecurrenceExtras(form: HTMLFormElement): void {
    const wrapper = form.querySelector("[data-recurring-extras]") as HTMLElement | null;
    const list = form.querySelector("[data-recurring-extra-list]") as HTMLElement | null;
    if (!wrapper || !list) return;
    const recurrenceToggle = form.elements.namedItem("recurringEnabled") as HTMLInputElement | null;
    list.innerHTML = "";
    if (!this.extras.length || !recurrenceToggle?.checked) { wrapper.hidden = true; return; }
    wrapper.hidden = false;
    const service = this.services.find((item) => item.id === this.serviceIdFromForm());
    list.innerHTML = this.extras.map((extra) => `<label class="extra-option"><input type="checkbox" name="recurring-extra-${escapeHtml(extra.id)}" value="${escapeHtml(extra.id)}" /> <span>${escapeHtml(extra.name)} <small>+${this.renderStaticPriceTooltip(extra.price_cents, service?.currency ?? "EUR", "Extra aplicado a cada ocurrencia de la serie.", "Precio del extra")}</small></span></label>`).join("");
    this.bindPriceTooltips(list);
    list.querySelectorAll<HTMLInputElement>('input[name^="recurring-extra-"]').forEach((input) => input.addEventListener("change", () => {
      const form = this.root.querySelector("form") as HTMLFormElement | null;
      if (form) this.scheduleRecurringPreviewRefresh(form);
    }));
  }

  private addRecurringParticipant(form: HTMLFormElement): void {
    const list = form.querySelector("[data-recurring-participant-list]") as HTMLElement | null;
    if (!list) return;
    const row = document.createElement("div");
    row.className = "participant-row";
    row.dataset.recurringParticipant = "true";
    row.innerHTML = '<input name="recurringParticipantName" placeholder="Nombre" aria-label="Nombre del participante" /><input name="recurringParticipantEmail" type="email" placeholder="Email (opcional)" aria-label="Email del participante" /><button type="button" class="icon-button" aria-label="Quitar participante">×</button>';
    row.querySelector("button")?.addEventListener("click", () => row.remove());
    list.append(row);
    row.querySelectorAll<HTMLInputElement>("input").forEach((input) => input.addEventListener("input", () => this.scheduleRecurringPreviewRefresh(form)));
  }

  private recurringParticipantsFromForm(form: HTMLFormElement): Array<{ name: string; email?: string }> {
    return [...form.querySelectorAll<HTMLElement>("[data-recurring-participant]")].map((row) => {
      const name = (row.querySelector<HTMLInputElement>('input[name="recurringParticipantName"]')?.value ?? "").trim();
      const email = (row.querySelector<HTMLInputElement>('input[name="recurringParticipantEmail"]')?.value ?? "").trim();
      return { name, ...(email ? { email } : {}) };
    }).filter((participant) => participant.name.length > 0);
  }

  private renderRecurringPreview(): void {
    const container = this.root.querySelector("[data-recurring-preview]") as HTMLElement | null;
    if (!container) return;
    if (!this.recurringPreview) {
      container.hidden = !this.recurringPricingLoading;
      container.setAttribute("aria-busy", String(this.recurringPricingLoading));
      container.innerHTML = this.recurringPricingLoading
        ? '<div class="loading-skeleton" role="status"><span style="width:82%"></span><span style="width:64%"></span><span style="width:73%"></span></div><p class="recurrence-hint"><span class="spinner" aria-hidden="true"></span> Verificando disponibilidad y precio…</p>'
        : "";
      delete container.dataset.previewKey;
      return;
    }
    const key = JSON.stringify(this.recurringPreview.occurrences.map((occurrence) => [occurrence.sequence, occurrence.requestedStartAt, occurrence.startAt, occurrence.endAt, occurrence.providerId, occurrence.locationId, occurrence.status, occurrence.adjusted, occurrence.adjustmentReason, occurrence.totalCents, occurrence.amountCents, occurrence.currency, occurrence.quoteId, occurrence.pricing?.discountCents, occurrence.pricing?.dynamicAdjustmentCents]));
    const renderKey = `${key}:loading=${this.recurringPricingLoading}`;
    container.hidden = false;
    container.setAttribute("aria-busy", String(this.recurringPricingLoading));
    if (container.dataset.previewKey === renderKey) {
      this.updateRecurringSelectionSummary();
      return;
    }
    container.dataset.previewKey = renderKey;
    const preview = this.recurringPreview;
    const existingSelections = new Map<number, { include: boolean; requestedStartAt?: string; providerId: string | null; locationId: string | null; adjustmentAccepted: boolean; quoteId?: string | null }>();
    container.querySelectorAll<HTMLElement>("[data-occurrence-row]").forEach((row) => {
      const sequence = Number(row.dataset.occurrenceRow);
      if (!Number.isInteger(sequence)) return;
      const date = row.querySelector<HTMLInputElement>("[data-occurrence-date]")?.value ?? "";
      const time = row.querySelector<HTMLInputElement>("[data-occurrence-time]")?.value ?? "";
      existingSelections.set(sequence, {
        include: row.querySelector<HTMLInputElement>("[data-occurrence-include]")?.checked ?? false,
        requestedStartAt: zonedDateTimeToIso(date, time, preview.timezone) ?? undefined,
        providerId: row.querySelector<HTMLSelectElement>("[data-occurrence-provider]")?.value || null,
        locationId: row.querySelector<HTMLSelectElement>("[data-occurrence-location]")?.value || null,
        adjustmentAccepted: row.querySelector<HTMLInputElement>("[data-occurrence-adjustment]")?.checked ?? false,
        quoteId: row.dataset.occurrenceQuoteId || null
      });
    });
    const providerOptions = (selected?: string | null) => `<option value="">Cualquier profesional</option>${this.providers.map((provider) => `<option value="${escapeHtml(provider.id)}"${provider.id === selected ? " selected" : ""}>${escapeHtml(provider.display_name)}</option>`).join("")}`;
    const locationOptions = (selected?: string | null) => `<option value="">Cualquier sede</option>${this.locations.map((location) => `<option value="${escapeHtml(location.id)}"${location.id === selected ? " selected" : ""}>${escapeHtml(location.name)}</option>`).join("")}`;
    const rows = preview.occurrences.map((occurrence) => {
      const unavailable = occurrence.status === "unavailable";
      const removed = occurrence.status === "cancelled";
      const adjusted = occurrence.adjusted;
      const previous = existingSelections.get(occurrence.sequence);
      const include = previous?.include ?? occurrence.status !== "cancelled";
      const adjustmentAccepted = !adjusted || previous?.adjustmentAccepted === true;
      const requested = this.formatOccurrenceDateTime(occurrence.requestedStartAt, preview.timezone);
      const actual = this.formatOccurrenceDateTime(occurrence.startAt, preview.timezone);
      const adjustmentReason = this.adjustmentReasonText(occurrence.adjustmentReason);
      const fields = zonedDateTimeFields(previous?.requestedStartAt ?? occurrence.requestedStartAt, preview.timezone);
      const requestedDate = fields?.date ?? "";
      const requestedTime = fields?.time ?? "";
      const totalCents = occurrence.totalCents ?? occurrence.pricing?.totalCents ?? occurrence.amountCents + occurrence.taxCents;
      const pricing = occurrence.pricing ?? null;
      const providerName = this.providers.find((provider) => provider.id === (occurrence.providerId ?? previous?.providerId))?.display_name ?? "Cualquier profesional";
      const locationName = this.locations.find((location) => location.id === (occurrence.locationId ?? previous?.locationId))?.name ?? "Cualquier sede";
      return `<article class="occurrence-row ${unavailable ? "unavailable" : ""} ${removed ? "removed" : ""}" data-occurrence-row="${occurrence.sequence}" data-occurrence-quote-id="${escapeHtml(occurrence.quoteId ?? previous?.quoteId ?? "")}"><label class="occurrence-toggle"><input type="checkbox" data-occurrence-include ${include ? "checked" : ""} aria-label="${include ? "Quitar" : "Restaurar"} ocurrencia ${occurrence.sequence + 1}" /><span>${include ? "Incluida" : "Restaurar"}</span></label><div class="occurrence-main"><strong>Fecha ${occurrence.sequence + 1}: ${escapeHtml(actual)}</strong><small>Profesional: ${escapeHtml(providerName)} · Sede: ${escapeHtml(locationName)}</small><small>Importe: ${this.renderPriceTooltip(totalCents, occurrence.currency, pricing, `Ocurrencia ${occurrence.sequence + 1}`)}</small>${adjusted ? `<div class="occurrence-adjustment" role="alert"><strong>Horario ajustado por falta de disponibilidad</strong><span>Solicitado: ${escapeHtml(requested)}</span><span>Propuesto: ${escapeHtml(actual)}</span><small>${escapeHtml(adjustmentReason)}</small></div>` : `<small>${unavailable ? "No hay disponibilidad para esta fecha; quitála o modificá sus datos para continuar." : removed ? "Esta ocurrencia fue quitada. Podés restaurarla." : "Fecha solicitada disponible"}</small>`}<div class="occurrence-controls"><label class="occurrence-field">Fecha<input type="date" data-occurrence-date value="${escapeHtml(requestedDate)}" required aria-label="Fecha de la ocurrencia ${occurrence.sequence + 1}" /></label><label class="occurrence-field">Hora<input type="time" data-occurrence-time value="${escapeHtml(requestedTime)}" required aria-label="Hora de la ocurrencia ${occurrence.sequence + 1}" /></label><label class="occurrence-field">Profesional<select data-occurrence-provider aria-label="Profesional de la ocurrencia ${occurrence.sequence + 1}">${providerOptions(occurrence.providerId ?? previous?.providerId)}</select></label><label class="occurrence-field">Sede<select data-occurrence-location aria-label="Sede de la ocurrencia ${occurrence.sequence + 1}">${locationOptions(occurrence.locationId ?? previous?.locationId)}</select></label></div>${adjusted ? `<label class="adjustment-confirm"><input type="checkbox" data-occurrence-adjustment ${adjustmentAccepted ? "checked" : ""} /> Acepto reservar el horario propuesto del ${escapeHtml(actual)}.</label>` : ""}</div></article>`;
    }).join("");
    container.innerHTML = `<div class="occurrence-actions"><h4>Previsualización de ${preview.occurrences.length} ocurrencias</h4>${preview.occurrences.length < 52 ? '<button type="button" class="secondary-button" data-action="add-occurrence">+ Agregar otra</button>' : ""}</div><div class="occurrence-list">${rows}</div><div class="price-summary"><span data-recurring-selection-summary>${preview.occurrences.filter((occurrence) => occurrence.status !== "cancelled").length} fechas seleccionadas</span><strong data-recurring-total>${this.renderPriceTooltip(preview.totalCents, preview.currency, preview.pricing ?? null, "Total de la serie")}</strong></div>${this.recurringPricingLoading ? '<p class="recurrence-hint status busy"><span class="spinner" aria-hidden="true"></span> Verificando disponibilidad y precio…</p>' : ""}<p class="recurrence-hint">Cada ocurrencia conserva su fecha, hora, profesional, sede y cotización. La serie se vuelve a validar antes de reservar.</p>`;
    this.bindPriceTooltips(container);
    container.querySelector('[data-action="add-occurrence"]')?.addEventListener("click", () => {
      const form = this.root.querySelector("form") as HTMLFormElement | null;
      if (form) this.changeRecurrenceCount(form, 1);
    });
    container.querySelectorAll<HTMLInputElement>("[data-occurrence-include]").forEach((input) => input.addEventListener("change", () => {
      const label = input.closest(".occurrence-toggle")?.querySelector("span");
      if (label) label.textContent = input.checked ? "Incluida" : "Restaurar";
      this.updateRecurringSelectionSummary();
      const form = this.root.querySelector("form") as HTMLFormElement | null;
      if (form) this.scheduleRecurringPreviewRefresh(form);
    }));
    const refreshFromOccurrenceInput = (input: HTMLInputElement): void => {
      const row = input.closest<HTMLElement>("[data-occurrence-row]");
      const adjustment = row?.querySelector<HTMLInputElement>("[data-occurrence-adjustment]");
      if (adjustment) adjustment.checked = false;
      const form = this.root.querySelector("form") as HTMLFormElement | null;
      if (form && !this.validateRecurringOccurrenceInputs()) return;
      this.updateRecurringSelectionSummary();
      if (form) this.scheduleRecurringPreviewRefresh(form);
    };
    container.querySelectorAll<HTMLInputElement>("[data-occurrence-date], [data-occurrence-time]").forEach((input) => {
      input.addEventListener("input", () => refreshFromOccurrenceInput(input));
      input.addEventListener("change", () => refreshFromOccurrenceInput(input));
    });
    container.querySelectorAll<HTMLSelectElement>("[data-occurrence-provider], [data-occurrence-location]").forEach((input) => input.addEventListener("change", () => {
      const row = input.closest<HTMLElement>("[data-occurrence-row]");
      const adjustment = row?.querySelector<HTMLInputElement>("[data-occurrence-adjustment]");
      if (adjustment) adjustment.checked = false;
      this.updateRecurringSelectionSummary();
      const form = this.root.querySelector("form") as HTMLFormElement | null;
      if (form) this.scheduleRecurringPreviewRefresh(form);
    }));
    container.querySelectorAll<HTMLInputElement>("[data-occurrence-adjustment]").forEach((input) => input.addEventListener("change", () => {
      this.updateRecurringSelectionSummary();
      this.updateStepUI();
    }));
    this.updateRecurringSelectionSummary();
  }

  private renderRecurringInformationSummary(): void {
    const container = this.root.querySelector("[data-recurring-information-summary]") as HTMLElement | null;
    const preview = this.recurringPreview;
    if (!container || !preview || this.currentStep !== "information") {
      if (container) { container.hidden = true; container.innerHTML = ""; }
      return;
    }
    const selected = this.selectedRecurringOccurrences().filter((occurrence) => occurrence.include);
    const selectedRows = preview.occurrences.filter((occurrence) => selected.some((item) => item.sequence === occurrence.sequence));
    const locale = this.getAttribute("locale") ?? "es-ES";
    const dates = selectedRows.map((occurrence) => {
      const date = new Intl.DateTimeFormat(locale, { dateStyle: "medium", timeStyle: "short", timeZone: preview.timezone }).format(new Date(occurrence.startAt));
      const totalCents = occurrence.totalCents ?? occurrence.pricing?.totalCents ?? occurrence.amountCents + occurrence.taxCents;
      return `<li>${escapeHtml(date)} · ${this.renderPriceTooltip(totalCents, occurrence.currency, occurrence.pricing ?? null, `Ocurrencia ${occurrence.sequence + 1}`)}</li>`;
    }).join("");
    container.hidden = false;
    const total = this.recurringPricingLoading ? null : preview.totalCents;
    container.innerHTML = `<header><strong>Serie de ${selectedRows.length} fecha${selectedRows.length === 1 ? "" : "s"}</strong><span>${total === null ? '<span class="spinner" aria-hidden="true"></span> Recalculando…' : this.renderPriceTooltip(total, preview.currency, preview.pricing ?? null, "Total de la serie")}</span></header><ul>${dates || "<li>Elegí al menos una fecha.</li>"}</ul><p class="review-note">El importe de cada ocurrencia viene de la API y puede incluir reglas dinámicas, extras, cupón e impuestos.</p><button type="button" data-action="edit-recurring-dates">Editar fechas, profesional o sede</button>`;
    this.bindPriceTooltips(container);
    container.querySelector('[data-action="edit-recurring-dates"]')?.addEventListener("click", () => { this.currentStep = "recurrence"; this.updateStepUI(); });
  }

  private updateRecurringSelectionSummary(): void {
    const preview = this.recurringPreview;
    const container = this.root.querySelector("[data-recurring-preview]") as HTMLElement | null;
    if (!preview || !container) return;
    const rows = [...container.querySelectorAll<HTMLElement>("[data-occurrence-row]")];
    const included = rows.filter((row) => row.querySelector<HTMLInputElement>("[data-occurrence-include]")?.checked);
    const summary = container.querySelector("[data-recurring-selection-summary]");
    const totalNode = container.querySelector("[data-recurring-total]");
    if (summary) summary.textContent = `${included.length} fecha${included.length === 1 ? "" : "s"} seleccionada${included.length === 1 ? "" : "s"}`;
    if (totalNode) totalNode.innerHTML = this.recurringPricingLoading ? '<span class="spinner" aria-hidden="true"></span> Recalculando…' : this.renderPriceTooltip(preview.totalCents, preview.currency, preview.pricing ?? null, "Total de la serie");
    if (totalNode && !this.recurringPricingLoading) this.bindPriceTooltips(totalNode);
  }

  private selectedRecurringOccurrences(): Array<{ sequence: number; include: boolean; quoteId?: string | null; requestedStartAt?: string; startAt: string; endAt: string; providerId?: string | null; locationId?: string | null; adjustmentAccepted?: boolean }> {
    const preview = this.recurringPreview;
    const container = this.root.querySelector("[data-recurring-preview]") as HTMLElement | null;
    if (!preview || !container) return [];
    return preview.occurrences.map((occurrence) => {
      const row = container.querySelector<HTMLElement>(`[data-occurrence-row="${occurrence.sequence}"]`);
      const requestedStartAt = zonedDateTimeToIso(
        row?.querySelector<HTMLInputElement>("[data-occurrence-date]")?.value ?? "",
        row?.querySelector<HTMLInputElement>("[data-occurrence-time]")?.value ?? "",
        preview.timezone
      ) ?? occurrence.requestedStartAt;
      return {
        sequence: occurrence.sequence,
        include: row?.querySelector<HTMLInputElement>("[data-occurrence-include]")?.checked ?? false,
        quoteId: row?.dataset.occurrenceQuoteId || occurrence.quoteId || null,
        requestedStartAt,
        startAt: occurrence.startAt,
        endAt: occurrence.endAt,
        providerId: row?.querySelector<HTMLSelectElement>("[data-occurrence-provider]")?.value || null,
        locationId: row?.querySelector<HTMLSelectElement>("[data-occurrence-location]")?.value || null,
        adjustmentAccepted: !occurrence.adjusted || Boolean(row?.querySelector<HTMLInputElement>("[data-occurrence-adjustment]")?.checked)
      };
    });
  }

  private validateRecurringOccurrenceInputs(): boolean {
    const preview = this.recurringPreview;
    const container = this.root.querySelector("[data-recurring-preview]") as HTMLElement | null;
    if (!preview || !container) return true;
    let valid = true;
    container.querySelectorAll<HTMLElement>("[data-occurrence-row]").forEach((row) => {
      const date = row.querySelector<HTMLInputElement>("[data-occurrence-date]");
      const time = row.querySelector<HTMLInputElement>("[data-occurrence-time]");
      const requestedStartAt = zonedDateTimeToIso(date?.value ?? "", time?.value ?? "", preview.timezone);
      if (!requestedStartAt) {
        valid = false;
        time?.setCustomValidity("Elegí una fecha y hora válidas para esta ocurrencia.");
      } else {
        time?.setCustomValidity("");
      }
    });
    if (!valid) this.setStatus("Revisá la fecha y hora de cada ocurrencia antes de continuar.", true);
    return valid;
  }

  private recurringCountFromForm(form: HTMLFormElement, fallback: number): number {
    const raw = Number((form.elements.namedItem("recurrenceCount") as HTMLInputElement | null)?.value);
    if (!Number.isFinite(raw)) return fallback;
    return Math.min(52, Math.max(1, Math.trunc(raw)));
  }

  private changeRecurrenceCount(form: HTMLFormElement, delta: number): void {
    const input = form.elements.namedItem("recurrenceCount") as HTMLInputElement | null;
    if (!input) return;
    const current = this.recurringCountFromForm(form, 4);
    const next = Math.min(52, Math.max(1, current + Math.trunc(delta)));
    if (next === current) return;
    input.value = String(next);
    this.scheduleRecurringPreviewRefresh(form);
  }

  private couponCodeFromForm(form: HTMLFormElement): string | undefined {
    const review = form.querySelector<HTMLInputElement>("[data-review-coupon]");
    const legacy = form.elements.namedItem("couponCode") as HTMLInputElement | null;
    const value = (review?.value ?? legacy?.value ?? "").trim();
    return value || undefined;
  }

  private oneOffEndAt(form: HTMLFormElement, slot: AvailableSlot, service: ServiceSummary): string {
    const extraDuration = this.selectedExtrasFromForm(form).reduce((total, selected) => {
      const extra = this.extras.find((item) => item.id === selected.extraId);
      return total + (extra?.duration_minutes ?? 0) * selected.quantity;
    }, 0);
    return new Date(Date.parse(slot.startAt) + (service.durationMinutes + extraDuration) * 60_000).toISOString();
  }

  private renderStaticPriceTooltip(amountCents: number, currency: string, description: string, label = "Detalle del precio"): string {
    return this.renderPriceTooltip(amountCents, currency, null, label, description);
  }

  private renderPriceTooltip(amountCents: number, currency: string, breakdown?: PriceQuoteBreakdown | null, label = "Detalle del precio", extraDescription?: string): string {
    const lines = breakdown?.lines?.length
      ? `<ul>${breakdown.lines.map((line) => `<li>${escapeHtml(line.label)} × ${line.quantity}: <strong>${escapeHtml(this.formatMoney(line.totalCents, currency))}</strong></li>`).join("")}</ul>`
      : "";
    const dynamic = breakdown?.dynamicAdjustmentCents ?? 0;
    const discount = breakdown?.discountCents ?? 0;
    const tax = breakdown?.taxCents ?? 0;
    const details = breakdown
      ? `${lines}<footer><span>Precio base <b>${escapeHtml(this.formatMoney(breakdown.subtotalCents - dynamic, currency))}</b></span>${dynamic ? `<span class="${dynamic > 0 ? "positive" : "negative"}">Ajuste dinámico <b>${escapeHtml(this.formatMoney(dynamic, currency))}</b></span>` : ""}${breakdown.appliedRules.map((rule) => `<span>${escapeHtml(rule.label || "Regla de precio")} <b>${escapeHtml(rule.reason)}</b></span>`).join("")}${discount ? `<span class="negative">Cupón / descuento <b>${escapeHtml(this.formatMoney(-discount, currency))}</b></span>` : ""}${tax ? `<span>Impuestos <b>${escapeHtml(this.formatMoney(tax, currency))}</b></span>` : ""}<span>Total <b>${escapeHtml(this.formatMoney(breakdown.totalCents, currency))}</b></span><span>Depósito <b>${escapeHtml(this.formatMoney(breakdown.depositCents, currency))}</b></span></footer>`
      : `<p>${escapeHtml(extraDescription ?? "Precio configurado por el negocio.")}</p>`;
    return `<span class="price-with-tooltip" tabindex="0" role="img" aria-label="${escapeHtml(`${label}: ${this.formatMoney(amountCents, currency)}`)}" aria-expanded="false" data-price-tooltip><span>${escapeHtml(this.formatMoney(amountCents, currency))}</span><span class="price-tooltip" role="tooltip"><strong>${escapeHtml(label)}</strong>${details}</span></span>`;
  }

  private bindPriceTooltips(scope: ParentNode = this.root): void {
    scope.querySelectorAll<HTMLElement>("[data-price-tooltip]").forEach((node) => {
      if (node.dataset.tooltipBound === "true") return;
      node.dataset.tooltipBound = "true";
      const close = (event?: Event) => {
        if (event && node.contains(event.target as Node)) return;
        node.classList.remove("open");
        node.setAttribute("aria-expanded", "false");
      };
      node.addEventListener("click", () => {
        const open = !node.classList.contains("open");
        node.classList.toggle("open", open);
        node.setAttribute("aria-expanded", String(open));
      });
      node.addEventListener("keydown", (event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          node.click();
        }
        if (event.key === "Escape") close();
      });
      node.addEventListener("blur", () => window.setTimeout(() => close(), 0));
    });
  }

  private highlightPrices(): void {
    this.root.querySelectorAll<HTMLElement>(".price-with-tooltip").forEach((node) => node.classList.add("price-updated"));
    if (this.pricePulseTimer) window.clearTimeout(this.pricePulseTimer);
    this.pricePulseTimer = window.setTimeout(() => {
      this.root.querySelectorAll<HTMLElement>(".price-with-tooltip").forEach((node) => node.classList.remove("price-updated"));
      this.pricePulseTimer = undefined;
    }, 1_200);
  }

  private cancelRecurringPreviewDebounce(): void {
    if (this.recurringPreviewDebounceTimer !== undefined) {
      window.clearTimeout(this.recurringPreviewDebounceTimer);
      this.recurringPreviewDebounceTimer = undefined;
    }
    this.reviewRefreshPending = false;
  }

  private clearStatusBusy(): void {
    const status = this.root.querySelector('[role="status"]') as HTMLElement | null;
    if (!status) return;
    status.setAttribute("aria-busy", "false");
    status.classList.remove("busy");
    status.querySelector(".spinner")?.remove();
  }

  private cancelRecurringPreviewRequest(options: { clearPreview?: boolean } = {}): void {
    this.cancelRecurringPreviewDebounce();
    this.recurringPreviewRequestSequence += 1;
    this.recurringPreviewAbort?.abort();
    this.recurringPreviewAbort = undefined;
    this.recurringPricingLoading = false;
    if (options.clearPreview) this.recurringPreview = undefined;
    this.renderRecurringPreview();
    this.clearStatusBusy();
    this.updateStepUI();
  }

  private scheduleRecurringPreviewRefresh(form: HTMLFormElement): void {
    if (!this.selectedSlot || !this.serviceRecurrence()?.enabled || !["recurrence", "information", "payment"].includes(this.currentStep)) return;
    this.cancelRecurringPreviewDebounce();
    this.reviewRefreshPending = true;
    const submit = this.root.querySelector<HTMLButtonElement>('[data-action="continue"]');
    if (submit) submit.disabled = true;
    this.setStatus("Verificando disponibilidad y precio…", false, true);
    this.recurringPreviewDebounceTimer = window.setTimeout(() => {
      this.recurringPreviewDebounceTimer = undefined;
      if (!this.validateRecurringOccurrenceInputs()) {
        this.reviewRefreshPending = false;
        this.updateStepUI();
        return;
      }
      void this.refreshRecurringPreviewForSelection(form);
    }, 350);
  }

  private scheduleReviewQuoteRefresh(form: HTMLFormElement): void {
    this.cancelRecurringPreviewDebounce();
    this.reviewRefreshPending = true;
    const submit = this.root.querySelector<HTMLButtonElement>('[data-action="continue"]');
    if (submit) submit.disabled = true;
    if (!this.recurringPreview) this.oneOffQuote = undefined;
    this.setStatus("Verificando disponibilidad y precio…", false, true);
    this.recurringPreviewDebounceTimer = window.setTimeout(() => {
      this.recurringPreviewDebounceTimer = undefined;
      if (this.recurringPreview || (form.elements.namedItem("recurringEnabled") as HTMLInputElement | null)?.checked) {
        void this.refreshRecurringPreviewForSelection(form);
      } else {
        void this.refreshOneOffQuote(form);
      }
    }, 350);
  }

  private async refreshOneOffQuote(form: HTMLFormElement): Promise<void> {
    const slot = this.selectedSlot;
    const service = this.services.find((item) => item.id === this.serviceIdFromForm());
    if (!slot || !service || !this.client) {
      this.reviewRefreshPending = false;
      this.updateStepUI();
      return;
    }
    const sequence = ++this.oneOffQuoteRequestSequence;
    this.reviewQuoteAbort?.abort();
    const controller = new AbortController();
    this.reviewQuoteAbort = controller;
    this.oneOffQuoteLoading = true;
    this.setStatus("Verificando disponibilidad y precio…", false, true);
    this.updateStepUI();
    try {
      const persons = Math.max(1, Number((form.elements.namedItem("persons") as HTMLInputElement | null)?.value || 1) || 1);
      const endAt = this.oneOffEndAt(form, slot, service);
      const quote = await this.client.quoteService({
        serviceId: service.id,
        providerId: slot.providerId ?? null,
        locationId: slot.locationId ?? null,
        startAt: slot.startAt,
        endAt,
        persons,
        extras: this.selectedExtrasFromForm(form),
        couponCode: this.couponCodeFromForm(form),
        slotKey: slot.slotKey,
        timezone: slot.timezone ?? "UTC",
        idempotencyKey: randomIdempotency("public-review-quote")
      }, { signal: controller.signal });
      if (sequence !== this.oneOffQuoteRequestSequence) return;
      this.oneOffQuote = quote;
      this.highlightPrices();
      this.setStatus("Precio actualizado. Revisá el detalle antes de confirmar.");
    } catch (error) {
      if (sequence !== this.oneOffQuoteRequestSequence || isAbortError(error)) return;
      this.setStatus(this.errorMessage(error), true);
    } finally {
      if (this.reviewQuoteAbort === controller) this.reviewQuoteAbort = undefined;
      if (sequence === this.oneOffQuoteRequestSequence) {
        this.oneOffQuoteLoading = false;
        this.reviewRefreshPending = false;
        this.updateStepUI();
      }
    }
  }

  private async refreshRecurringPreviewForSelection(form: HTMLFormElement): Promise<void> {
    const preview = this.recurringPreview;
    const slot = this.selectedSlot;
    if (!slot || !this.client) {
      this.recurringPricingLoading = false;
      this.reviewRefreshPending = false;
      this.updateStepUI();
      return;
    }
    const previousSelections = preview ? this.selectedRecurringOccurrences() : undefined;
    const sequence = ++this.recurringPreviewRequestSequence;
    this.recurringPreviewAbort?.abort();
    const controller = new AbortController();
    this.recurringPreviewAbort = controller;
    this.recurringPricingLoading = true;
    this.renderRecurringPreview();
    this.updateStepUI();
    let timedOut = false;
    const timeout = window.setTimeout(() => { timedOut = true; controller.abort(); }, 15_000);
    try {
      this.setStatus("Verificando disponibilidad y precio de las fechas…", false, true);
      const rule = this.recurringRuleFromForm(form);
      const values = Object.fromEntries(new FormData(form).entries());
      const refreshed = await this.client.previewRecurringSeries({
        serviceId: this.serviceIdFromForm(), providerId: slot.providerId ?? null, locationId: slot.locationId ?? null,
        timezone: slot.timezone ?? "UTC", startsAt: slot.startAt, count: this.recurringCountFromForm(form, preview?.occurrences.length ?? 4), rule,
        persons: Math.max(1, Number(values.persons ?? 1) || 1), participants: this.recurringParticipantsFromForm(form),
        extras: this.selectedExtrasFromForm(form, true), couponCode: this.couponCodeFromForm(form),
        occurrences: previousSelections
      }, { signal: controller.signal });
      if (sequence !== this.recurringPreviewRequestSequence) return;
      this.recurringPreview = refreshed;
      this.renderRecurringPreview();
      this.highlightPrices();
      this.setStatus("Disponibilidad y precio actualizados. Revisá cualquier ajuste antes de continuar.");
    } catch (error) {
      if (sequence !== this.recurringPreviewRequestSequence) return;
      this.setStatus(timedOut ? "La previsualización tardó demasiado. Reintentá." : this.errorMessage(error), true);
    } finally {
      window.clearTimeout(timeout);
      if (this.recurringPreviewAbort === controller) this.recurringPreviewAbort = undefined;
      if (sequence === this.recurringPreviewRequestSequence) {
        this.recurringPricingLoading = false;
        this.reviewRefreshPending = false;
        this.renderRecurringPreview();
        this.updateStepUI();
      }
    }
  }

  private async goToStep(target: BookingStep): Promise<void> {
    const targetIndex = bookingSteps.findIndex((step) => step.key === target);
    const currentIndex = bookingSteps.findIndex((step) => step.key === this.currentStep);
    if (target === "confirmation" || targetIndex < 0 || targetIndex > currentIndex) return;
    if (target === "information" && !this.pendingHold && !this.recurringPreview) { this.setStatus("Elegí un horario y continuá para completar tus datos.", true); return; }
    if (target === "payment" && !this.recurringCheckout && !this.bookingCheckout && !this.selectedSlot) { this.setStatus("Elegí un horario antes de revisar el pago.", true); return; }
    this.currentStep = target;
    this.updateStepUI();
  }

  private async goPrevious(): Promise<void> {
    if (this.currentStep === "confirmation" || this.currentStep === "datetime") return;
    if (this.currentStep === "information") {
      if (this.recurringPreview) this.currentStep = "recurrence";
      else { await this.releasePendingHold(); this.currentStep = "datetime"; }
    } else if (this.currentStep === "recurrence") {
      this.cancelRecurringPreviewRequest({ clearPreview: true });
      this.currentStep = "datetime";
    } else if (this.currentStep === "payment") {
      if (this.bookingCheckout) {
        this.setStatus("La reserva está pendiente de pago. Abrí el enlace de Mercado Pago para continuar.");
        return;
      }
      this.currentStep = "information";
    }
    this.updateStepUI();
  }

  private changeMonth(offset: number): void {
    const date = monthStart(this.calendarMonth);
    date.setMonth(date.getMonth() + offset);
    this.calendarMonth = monthValue(date);
    this.renderCalendar();
    void this.loadCalendarSummary();
  }

  private renderCalendar(): void {
    const grid = this.root.querySelector("[data-calendar-grid]") as HTMLElement | null;
    const monthSelect = this.root.querySelector("[data-calendar-month]") as HTMLSelectElement | null;
    const yearSelect = this.root.querySelector("[data-calendar-year]") as HTMLSelectElement | null;
    if (!grid || !monthSelect || !yearSelect) return;
    const visibleMonth = monthStart(this.calendarMonth);
    const locale = this.getAttribute("locale") ?? "es-ES";
    const monthFormatter = new Intl.DateTimeFormat(locale, { month: "long" });
    const months = Array.from({ length: 12 }, (_, index) => `<option value="${index}" ${index === visibleMonth.getMonth() ? "selected" : ""}>${escapeHtml(monthFormatter.format(new Date(2020, index, 1)))}</option>`).join("");
    monthSelect.innerHTML = months;
    monthSelect.value = String(visibleMonth.getMonth());
    const yearOptions = Array.from({ length: 5 }, (_, index) => visibleMonth.getFullYear() - 2 + index).map((year) => `<option value="${year}" ${year === visibleMonth.getFullYear() ? "selected" : ""}>${year}</option>`).join("");
    yearSelect.innerHTML = yearOptions;
    yearSelect.value = String(visibleMonth.getFullYear());
    const changeMonth = () => {
      this.calendarMonth = `${yearSelect.value}-${String(Number(monthSelect.value) + 1).padStart(2, "0")}`;
      this.renderCalendar();
      void this.loadCalendarSummary();
    };
    monthSelect.onchange = changeMonth;
    yearSelect.onchange = changeMonth;
    const firstDay = new Date(visibleMonth.getFullYear(), visibleMonth.getMonth(), 1);
    const offset = (firstDay.getDay() + 6) % 7;
    const today = localDateValue();
    const cells: string[] = [];
    for (let index = 0; index < 42; index += 1) {
      const date = new Date(visibleMonth.getFullYear(), visibleMonth.getMonth(), 1 - offset + index);
      const value = localDateValue(date);
      const inMonth = date.getMonth() === visibleMonth.getMonth();
      const past = value < today;
      const knownUnavailable = inMonth && this.availabilityDays.has(value) && this.availabilityDays.get(value) === 0;
      const disabled = !inMonth || past || knownUnavailable;
      const count = this.availabilityDays.get(value);
      const classes = ["calendar-day", inMonth ? "" : "outside", past ? "past" : "", knownUnavailable ? "unavailable" : "", value === this.selectedDate ? "selected" : "", value === today ? "today" : ""].filter(Boolean).join(" ");
      cells.push(`<button type="button" class="${classes}" data-calendar-date="${value}" ${disabled ? "disabled" : ""} aria-label="${escapeHtml(new Intl.DateTimeFormat(locale, { dateStyle: "full" }).format(date))}"${value === this.selectedDate ? " aria-pressed=\"true\"" : ""}><span>${date.getDate()}</span><small>${count && count > 0 ? count : ""}</small></button>`);
    }
    grid.innerHTML = cells.join("");
    grid.querySelectorAll("[data-calendar-date]").forEach((node) => node.addEventListener("click", () => {
      const value = (node as HTMLElement).dataset.calendarDate;
      if (!value) return;
      this.selectedDate = value;
      this.calendarMonth = value.slice(0, 7);
      const dateInput = this.root.querySelector('input[name="date"]') as HTMLInputElement | null;
      if (dateInput) dateInput.value = value;
       this.currentStep = "datetime";
       this.renderCalendar();
       void this.loadSlots();
       window.setTimeout(() => this.root.querySelector("[data-slots]")?.scrollIntoView({ behavior: "smooth", block: "start" }), 0);
    }));
  }

  private renderServiceScopedFilters(form: HTMLFormElement, providerRows: CatalogProvider[], locationRows: CatalogLocation[], selectedProviderId?: string, selectedLocationId?: string): void {
    const serviceId = this.serviceIdFromForm();
    const providerSelect = form.elements.namedItem("providerId") as HTMLSelectElement | null;
    if (providerSelect) {
      providerSelect.innerHTML = "";
      providerSelect.append(new Option(
        !serviceId ? "Elegí un servicio primero" : providerRows.length ? "Todos los profesionales" : "No hay profesionales para este servicio",
        ""
      ));
      providerRows.forEach((provider) => providerSelect.append(new Option(provider.display_name, provider.id)));
      providerSelect.value = selectedProviderId && providerRows.some((provider) => provider.id === selectedProviderId) ? selectedProviderId : "";
      providerSelect.disabled = !serviceId || providerRows.length === 0;
    }
    const locationSelect = form.elements.namedItem("locationId") as HTMLSelectElement | null;
    if (locationSelect) {
      locationSelect.innerHTML = "";
      locationSelect.append(new Option(
        !serviceId ? "Elegí un servicio primero" : locationRows.length ? "Todas las sedes" : "No hay sedes para este servicio",
        ""
      ));
      locationRows.forEach((location) => locationSelect.append(new Option(`${location.name} · ${location.timezone}`, location.id)));
      locationSelect.value = selectedLocationId && locationRows.some((location) => location.id === selectedLocationId) ? selectedLocationId : "";
      locationSelect.disabled = !serviceId || locationRows.length === 0;
    }
  }

  private async loadCatalogAndSlots(): Promise<void> {
    if (!this.client) return;
    const form = this.root.querySelector("form") as HTMLFormElement | null;
    if (!form) return;
    this.catalogAbort?.abort();
    const controller = new AbortController();
    this.catalogAbort = controller;
    const sequence = ++this.catalogRequestSequence;
    const timeout = window.setTimeout(() => controller.abort(), 10_000);
    try {
      const serviceSelect = form.elements.namedItem("serviceId") as HTMLSelectElement;
      const providerSelect = form.elements.namedItem("providerId") as HTMLSelectElement | null;
      const locationSelect = form.elements.namedItem("locationId") as HTMLSelectElement | null;
      const selectedServiceId = this.getAttribute("service-id") ?? serviceSelect.value;
      const selectedProviderId = (this.getAttribute("provider-id") ?? providerSelect?.value) || undefined;
      const selectedLocationId = (this.getAttribute("location-id") ?? locationSelect?.value) || undefined;
      const serviceRows = await this.client.services({ signal: controller.signal });
      if (sequence !== this.catalogRequestSequence) return;
      this.services = serviceRows;
      const retry = this.root.querySelector("[data-catalog-retry]") as HTMLElement | null;
      if (retry) retry.hidden = true;
      if (this.getAttribute("service-id")) {
        const service = serviceRows.find((item) => item.id === selectedServiceId);
        serviceSelect.innerHTML = `<option value="${escapeHtml(selectedServiceId)}">${escapeHtml(service?.name ?? "Servicio seleccionado")}</option>`;
        serviceSelect.value = selectedServiceId;
        serviceSelect.disabled = true;
      } else {
        serviceSelect.innerHTML = `<option value="">Seleccioná un servicio</option>`;
        serviceRows.forEach((service) => serviceSelect.append(new Option(`${service.name} · ${service.durationMinutes} min`, service.id)));
        if (selectedServiceId && serviceRows.some((service) => service.id === selectedServiceId)) serviceSelect.value = selectedServiceId;
      }
      const serviceId = this.getAttribute("service-id") ?? serviceSelect.value;
      let providerRows: CatalogProvider[] = [];
      let locationRows: CatalogLocation[] = [];
      if (serviceId) {
        [providerRows, locationRows] = await Promise.all([
          this.client.providers({ serviceId, signal: controller.signal }).catch((error) => {
            if (isAbortError(error)) throw error;
            return [];
          }),
          this.client.locations({ serviceId, providerId: selectedProviderId, signal: controller.signal }).catch((error) => {
            if (isAbortError(error)) throw error;
            return [];
          })
        ]);
      }
      if (sequence !== this.catalogRequestSequence) return;
      this.providers = providerRows;
      this.locations = locationRows;
      this.renderServiceScopedFilters(form, providerRows, locationRows, selectedProviderId, selectedLocationId);
      const filters = form.querySelector(".filter-grid") as HTMLElement | null;
      if (filters) filters.hidden = false;
      this.updateServiceSummary(form);
      if (serviceId) {
        this.extras = await this.client.extras(serviceId, { signal: controller.signal }).catch((error) => {
          if (isAbortError(error)) throw error;
          return [];
        });
        if (sequence !== this.catalogRequestSequence) return;
        this.renderExtras(form);
        this.updateRecurrenceAvailability(form);
        this.updateStepUI();
        await this.loadSlots();
      } else {
        this.extras = [];
        this.renderExtras(form);
        this.currentStep = "datetime";
        this.setStatus("Elegí un servicio para consultar horarios.");
        this.renderSlots([]);
        this.updateStepUI();
      }
    } catch (error) {
      if (sequence !== this.catalogRequestSequence) return;
      const message = isAbortError(error) ? "La consulta del catálogo tardó demasiado." : this.errorMessage(error);
      this.setStatus(`${message} Usá Reintentar para volver a consultar.`, true);
      const serviceSelect = form.elements.namedItem("serviceId") as HTMLSelectElement | null;
      if (serviceSelect && !this.getAttribute("service-id")) serviceSelect.innerHTML = "<option value=\"\">No se pudo cargar el catálogo</option>";
      const retry = this.root.querySelector("[data-catalog-retry]") as HTMLElement | null;
      if (retry) retry.hidden = false;
      const choice = this.root.querySelector("[data-service-choice]") as HTMLElement | null;
      if (choice) choice.hidden = false;
      const slotsContainer = form.querySelector("[data-slots]") as HTMLElement | null;
      if (slotsContainer) {
        slotsContainer.innerHTML = `<div class="alert">${escapeHtml(message)}</div>`;
      }
    } finally {
      window.clearTimeout(timeout);
      if (this.catalogAbort === controller) this.catalogAbort = undefined;
    }
  }

  private updateServiceSummary(form: HTMLFormElement): void {
    const serviceId = this.getAttribute("service-id") ?? (form.elements.namedItem("serviceId") as HTMLSelectElement | null)?.value;
    const service = this.services.find((item) => item.id === serviceId);
    const meta = form.querySelector("[data-service-meta]") as HTMLElement | null;
    const choice = form.querySelector("[data-service-choice]") as HTMLElement | null;
    const description = form.querySelector("[data-service-description]") as HTMLElement | null;
    const payment = form.querySelector("[data-service-payment]") as HTMLElement | null;
    const price = form.querySelector("[data-service-price]") as HTMLElement | null;
    if (choice) choice.hidden = !service;
    if (!meta || !description || !price) { this.updateRecurrenceAvailability(form); return; }
    meta.hidden = !service;
    if (service) {
      description.textContent = `${service.description || "Duración"} · ${service.durationMinutes} min`;
      if (payment) payment.textContent = service.paymentMode === "prepay" ? "Mercado Pago · prepago" : "Pago posterior";
      price.innerHTML = this.renderStaticPriceTooltip(service.priceCents, service.currency, "Precio base configurado para el servicio.", "Precio base del servicio");
      this.bindPriceTooltips(price);
    }
    this.updateRecurrenceAvailability(form);
  }

  private async handleServiceChange(form: HTMLFormElement, serviceId: string): Promise<void> {
    this.oneOffQuote = undefined;
    this.cancelRecurringPreviewRequest({ clearPreview: true });
    this.recurringPreview = undefined;
    const providerSelect = form.elements.namedItem("providerId") as HTMLSelectElement | null;
    const locationSelect = form.elements.namedItem("locationId") as HTMLSelectElement | null;
    if (providerSelect && !this.getAttribute("provider-id")) providerSelect.value = "";
    if (locationSelect && !this.getAttribute("location-id")) locationSelect.value = "";
    this.providers = [];
    this.locations = [];
    this.renderServiceScopedFilters(form, [], []);
    this.extras = [];
    this.renderExtras(form);
    this.updateServiceSummary(form);
    if (!serviceId) {
      this.currentStep = "datetime";
      this.renderSlots([]);
      this.updateStepUI();
      this.setStatus("Elegí un servicio para consultar horarios.");
      return;
    }
    this.currentStep = "datetime";
    this.updateStepUI();
    this.setStatus("Cargando opciones del servicio…", false, true);
    await this.loadCatalogAndSlots();
  }

  private async loadServiceExtras(form: HTMLFormElement, serviceId: string): Promise<void> {
    if (!this.client) return;
    this.serviceExtrasAbort?.abort();
    const controller = new AbortController();
    this.serviceExtrasAbort = controller;
    const sequence = ++this.serviceExtrasRequestSequence;
    try {
      const extras = await this.client.extras(serviceId, { signal: controller.signal });
      if (sequence !== this.serviceExtrasRequestSequence || this.serviceIdFromForm() !== serviceId) return;
      this.extras = extras;
      this.renderExtras(form);
      this.updateRecurrenceAvailability(form);
    } catch (error) {
      if (sequence !== this.serviceExtrasRequestSequence || isAbortError(error)) return;
      this.extras = [];
      this.renderExtras(form);
      this.updateRecurrenceAvailability(form);
      this.setStatus("No pudimos cargar los extras del servicio. Podés continuar sin extras.", true);
    } finally {
      if (this.serviceExtrasAbort === controller) this.serviceExtrasAbort = undefined;
    }
  }

  private renderExtras(form: HTMLFormElement): void {
    const wrapper = form.querySelector("[data-extras]") as HTMLElement | null;
    const list = form.querySelector("[data-extra-list]") as HTMLElement | null;
    if (!wrapper || !list) return;
    const recurrenceToggle = form.elements.namedItem("recurringEnabled") as HTMLInputElement | null;
    list.innerHTML = "";
    wrapper.hidden = !this.extras.length || Boolean(recurrenceToggle?.checked);
    const serviceId = this.serviceIdFromForm();
    const currency = this.services.find((service) => service.id === serviceId)?.currency ?? "EUR";
    if (!wrapper.hidden) {
      list.innerHTML = this.extras.map((extra) => `<label class="extra-option"><input type="checkbox" name="extra-${escapeHtml(extra.id)}" value="${escapeHtml(extra.id)}" /> <span>${escapeHtml(extra.name)} <small>+${this.renderStaticPriceTooltip(extra.price_cents, currency, "Extra agregado a esta reserva.", "Precio del extra")}</small></span></label>`).join("");
      this.bindPriceTooltips(list);
      list.querySelectorAll<HTMLInputElement>('input[name^="extra-"]').forEach((input) => input.addEventListener("change", () => {
        this.oneOffQuote = undefined;
        if (this.currentStep === "payment") this.scheduleReviewQuoteRefresh(form);
      }));
    }
    this.renderRecurrenceExtras(form);
  }

  private async loadSlots(options: { preserveStatus?: boolean } = {}): Promise<void> {
    if (!this.client) return;
    const form = this.root.querySelector("form") as HTMLFormElement | null;
    if (!form) return;
    const serviceId = this.serviceIdFromForm();
    const dateInput = form.elements.namedItem("date") as HTMLInputElement;
    const date = dateInput.value || this.selectedDate;
    const providerId = (this.getAttribute("provider-id") ?? (form.elements.namedItem("providerId") as HTMLSelectElement).value) || undefined;
    const locationId = (this.getAttribute("location-id") ?? (form.elements.namedItem("locationId") as HTMLSelectElement).value) || undefined;
    const slotsContainer = form.querySelector("[data-slots]") as HTMLElement;
    if (!serviceId) { this.setStatus("Elegí un servicio para consultar horarios."); this.renderSlots([]); this.updateStepUI(); return; }
    if (!date) { this.setStatus("Elegí una fecha para consultar horarios."); this.renderSlots([]); this.updateStepUI(); return; }
    this.selectedDate = date;
    this.calendarMonth = date.slice(0, 7);
    this.cancelRecurringPreviewRequest({ clearPreview: true });
    this.oneOffQuote = undefined;
    this.slotsAbort?.abort();
    const controller = new AbortController(); this.slotsAbort = controller;
    const sequence = ++this.requestSequence;
    await this.releasePendingHold();
    const timeout = window.setTimeout(() => controller.abort(), 10_000);
    this.availabilityLoading = true;
    slotsContainer.setAttribute("aria-busy", "true");
    slotsContainer.innerHTML = '<div class="loading-skeleton" role="status"><span style="width:68%"></span><span style="width:84%"></span><span style="width:51%"></span></div><div class="slots-empty"><span class="spinner" aria-hidden="true"></span> Verificando disponibilidad…</div>';
    this.selectedSlot = undefined;
    this.updateStepUI();
    if (!options.preserveStatus) this.setStatus("Consultando disponibilidad…", false, true);
    try {
      const persons = Math.max(1, Number((form.elements.namedItem("persons") as HTMLInputElement | null)?.value || 1) || 1);
      const extras = this.selectedExtrasFromForm(form);
      let slots = await this.client.slots(serviceId, date, { providerId, locationId, persons, extras, includePricing: true, signal: controller.signal });
      if (sequence !== this.requestSequence) return;
      if (!slots.length && !providerId && !locationId) {
        const next = await this.client.nextAvailability(serviceId, { fromDate: date, days: 14, signal: controller.signal });
        if (next.nextDate && next.nextDate !== date) {
          this.selectedDate = next.nextDate;
          this.calendarMonth = next.nextDate.slice(0, 7);
          dateInput.value = next.nextDate;
          if (!options.preserveStatus) this.setStatus(`No había horarios el ${date}. Mostramos la próxima fecha disponible: ${next.nextDate}.`);
          slots = await this.client.slots(serviceId, next.nextDate, { providerId, locationId, persons, extras, includePricing: true, signal: controller.signal });
        }
      }
      if (sequence !== this.requestSequence) return;
      this.availabilityDays.set(this.selectedDate, slots.length);
      this.renderCalendar();
      this.renderSlots(slots);
      if (!options.preserveStatus) this.setStatus(slots.length ? `${slots.length} horarios disponibles.` : "No hay horarios disponibles. Probá otra fecha o filtro.");
      this.dispatchEvent(new CustomEvent("kuroo:availability-loaded", { bubbles: true, composed: true, detail: { serviceId, date: this.selectedDate, slots } }));
    } catch (error) {
      if (sequence !== this.requestSequence) return;
      this.renderSlots([], true);
      if (!options.preserveStatus) this.setStatus(isAbortError(error) ? "La consulta tardó demasiado. Reintentá." : this.errorMessage(error), true);
      this.dispatchEvent(new CustomEvent("kuroo:booking-error", { bubbles: true, composed: true, detail: { stage: "availability", error } }));
    } finally {
      window.clearTimeout(timeout);
      if (this.slotsAbort === controller) this.slotsAbort = undefined;
      this.availabilityLoading = false;
      slotsContainer.removeAttribute("aria-busy");
      this.updateStepUI();
    }
  }

  private async loadCalendarSummary(): Promise<void> {
    const serviceId = this.serviceIdFromForm();
    if (!this.client || !serviceId) return;
    const form = this.root.querySelector("form") as HTMLFormElement | null;
    const providerId = (this.getAttribute("provider-id") ?? (form?.elements.namedItem("providerId") as HTMLSelectElement | null)?.value) || undefined;
    const locationId = (this.getAttribute("location-id") ?? (form?.elements.namedItem("locationId") as HTMLSelectElement | null)?.value) || undefined;
    this.calendarAbort?.abort();
    const controller = new AbortController();
    this.calendarAbort = controller;
    const sequence = ++this.calendarRequestSequence;
    try {
      const result = await this.client.nextAvailability(serviceId, { fromDate: `${this.calendarMonth}-01`, days: 42, providerId, locationId, signal: controller.signal });
      if (sequence !== this.calendarRequestSequence) return;
      result.dates.forEach((entry) => this.availabilityDays.set(entry.date, entry.count));
      this.renderCalendar();
    } catch (error) {
      if (!isAbortError(error)) this.setStatus("No pudimos actualizar los días disponibles.");
    } finally {
      if (this.calendarAbort === controller) this.calendarAbort = undefined;
    }
  }

  private renderSlots(slots: AvailableSlot[], failed = false): void {
    const container = this.root.querySelector("[data-slots]") as HTMLElement | null;
    if (!container) return;
    this.selectedSlot = undefined;
    if (failed) { container.innerHTML = `<div class="alert">No se pudo cargar la disponibilidad. <button type="button" data-retry>Reintentar</button></div>`; container.querySelector("[data-retry]")?.addEventListener("click", () => void this.loadSlots()); return; }
    if (!slots.length) { container.innerHTML = "<div class=\"slots-empty\">Sin horarios para esta fecha.</div>"; this.updateStepUI(); return; }
    const groups = new Map<string, AvailableSlot[]>();
    slots.forEach((slot) => { const key = `${slot.providerName ?? "Profesional"} · ${slot.locationName ?? "Sede sin asignar"}`; groups.set(key, [...(groups.get(key) ?? []), slot]); });
    container.innerHTML = [...groups.entries()].map(([label, entries]) => `<div class="slot-group"><strong>${escapeHtml(label)}</strong><small>${escapeHtml(entries[0]?.timezone ?? "UTC")}</small><div class="slot-list">${entries.map((slot) => {
      const slotIndex = slots.indexOf(slot);
      const slotPrice = slot.quote ? this.renderPriceTooltip(slot.quote.totalCents, slot.quote.currency, slot.quote, "Precio estimado del turno") : "";
      return `<button type="button" class="slot" data-slot-index="${slotIndex}" data-slot-key="${escapeHtml(slot.slotKey)}">${this.formatSlot(slot)}${slot.remainingCapacity > 1 ? ` · ${slot.remainingCapacity} libres` : ""}${slotPrice ? `<span class="slot-price"> · ${slotPrice}</span>` : ""}</button>`;
    }).join("")}</div></div>`).join("");
    container.querySelectorAll("[data-slot-index]").forEach((button) => button.addEventListener("click", () => {
      const slotIndex = Number((button as HTMLElement).dataset.slotIndex);
      const slot = Number.isInteger(slotIndex) && slotIndex >= 0 ? slots[slotIndex] : undefined; if (!slot) return;
       container.querySelectorAll(".slot").forEach((item) => item.classList.remove("selected")); button.classList.add("selected"); this.selectedSlot = slot; this.oneOffQuote = slot.quote ?? undefined;
      this.currentStep = "datetime";
      this.updateStepUI();
      this.setStatus(`Seleccionaste ${this.formatSlot(slot)}. Continuá para reservarlo temporalmente.`);
    }));
    this.bindPriceTooltips(container);
  }

  private async findNextDate(): Promise<void> {
    const serviceId = this.serviceIdFromForm();
    if (!serviceId || !this.client) { this.setStatus("Elegí un servicio primero.", true); return; }
    const form = this.root.querySelector("form") as HTMLFormElement | null;
    const providerId = (this.getAttribute("provider-id") ?? (form?.elements.namedItem("providerId") as HTMLSelectElement | null)?.value) || undefined;
    const locationId = (this.getAttribute("location-id") ?? (form?.elements.namedItem("locationId") as HTMLSelectElement | null)?.value) || undefined;
    this.cancelRecurringPreviewRequest({ clearPreview: true });
    const fromDate = this.selectedDate || localDateValue();
    const button = this.root.querySelector('[data-action="next-date"]') as HTMLButtonElement;
    if (button) button.disabled = true;
    this.setStatus("Buscando el próximo horario…", false, true);
    this.slotsAbort?.abort();
    this.requestSequence += 1;
    const controller = new AbortController();
    this.slotsAbort = controller;
    const timeout = window.setTimeout(() => controller.abort(), 15_000);
    try {
      const next = await this.client.nextAvailability(serviceId, { fromDate, days: 90, providerId, locationId, signal: controller.signal });
      next.dates.forEach((entry) => this.availabilityDays.set(entry.date, entry.count));
      if (!next.nextDate) { this.setStatus("No encontramos disponibilidad en los próximos 90 días.", true); return; }
      this.selectedDate = next.nextDate;
      this.calendarMonth = next.nextDate.slice(0, 7);
      const dateInput = this.root.querySelector('input[name="date"]') as HTMLInputElement | null;
      if (dateInput) dateInput.value = next.nextDate;
      this.renderCalendar();
      await this.loadSlots();
    } catch (error) { this.setStatus(isAbortError(error) ? "La búsqueda tardó demasiado. Reintentá." : this.errorMessage(error), true); }
    finally { window.clearTimeout(timeout); if (this.slotsAbort === controller) this.slotsAbort = undefined; if (button) button.disabled = false; }
  }

  private formatSlot(slot: AvailableSlot): string {
    const locale = this.getAttribute("locale") ?? "es-ES";
    const formatter = new Intl.DateTimeFormat(locale, { hour: "2-digit", minute: "2-digit", timeZone: slot.timezone ?? undefined });
    return `${formatter.format(new Date(slot.startAt))}–${formatter.format(new Date(slot.endAt))}`;
  }

  private formatOccurrenceDateTime(value: string, timezone: string): string {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    try {
      return new Intl.DateTimeFormat(this.getAttribute("locale") ?? "es-ES", { dateStyle: "full", timeStyle: "short", timeZone: timezone }).format(date);
    } catch {
      return date.toLocaleString(this.getAttribute("locale") ?? "es-ES");
    }
  }

  private adjustmentReasonText(reason: string | null | undefined): string {
    switch (reason) {
      case "DATE_MOVED_NEXT_AVAILABLE":
        return "No había disponibilidad para el horario solicitado; se asignó la próxima fecha disponible según la regla de recurrencia.";
      case "DATE_MOVED_PREVIOUS_AVAILABLE":
        return "No había disponibilidad para el horario solicitado; se asignó la fecha disponible anterior según la regla de recurrencia.";
      case "TIME_MOVED_AVAILABLE_SLOT":
        return "No había disponibilidad para el horario solicitado; se asignó otro horario disponible.";
      default:
        return "No había disponibilidad para el horario solicitado; se asignó una alternativa disponible.";
    }
  }

  private formatMoney(cents: number, currency: string): string {
    try { return new Intl.NumberFormat(this.getAttribute("locale") ?? "es-AR", { style: "currency", currency }).format(cents / 100); }
    catch { return `${(cents / 100).toFixed(2)} ${currency}`; }
  }

  private setStatus(message: string, isError = false, busy = false): void {
    const status = this.root.querySelector('[role="status"]') as HTMLElement | null;
    if (!status) return;
    const isBusy = !isError && busy;
    status.innerHTML = isBusy ? `<span class="spinner" aria-hidden="true"></span> ${escapeHtml(message)}` : escapeHtml(message);
    status.setAttribute("aria-busy", String(isBusy));
    status.classList.toggle("busy", isBusy);
    status.classList.toggle("error", isError);
  }

  private errorMessage(error: unknown): string {
    if (error instanceof KurooApiError) return `${error.message} (${error.code})`;
    return error instanceof Error ? error.message : "No se pudo conectar con la disponibilidad.";
  }

  private async resetBooking(): Promise<void> {
    this.catalogAbort?.abort();
    this.catalogRequestSequence += 1;
    this.slotsAbort?.abort();
    this.calendarAbort?.abort();
    this.cancelRecurringPreviewRequest({ clearPreview: true });
    this.reviewQuoteAbort?.abort();
    this.requestSequence += 1;
    this.oneOffQuoteRequestSequence += 1;
    const release = this.releasePendingHold();
    if (this.paymentPollTimer) window.clearInterval(this.paymentPollTimer);
    this.paymentPollTimer = undefined;
    this.paymentPollAttempts = 0;
    this.selectedSlot = undefined;
    this.weeklyDaysCustomized = false;
    this.recurringCheckout = undefined;
    this.bookingCheckout = undefined;
    this.bookingPaymentStatus = undefined;
    this.oneOffQuote = undefined;
    this.oneOffQuoteLoading = false;
    this.availabilityLoading = false;
    this.recurringPricingLoading = false;
    this.paymentRefreshing = false;
    this.reviewRefreshPending = false;
    this.lastBooking = undefined;
    this.selectedDate = localDateValue();
    this.calendarMonth = monthValue(new Date());
    this.availabilityDays.clear();
    this.currentStep = "datetime";
    this.render();
    await release;
    await this.loadCatalogAndSlots();
  }

  private async releasePendingHold(): Promise<void> {
    const hold = this.pendingHold; this.pendingHold = undefined; if (this.countdownTimer) window.clearInterval(this.countdownTimer); this.countdownTimer = undefined;
    if (hold && this.client) {
      const release = this.client.releaseHold(hold.holdId, hold.slotKey).catch(() => undefined);
      await Promise.race([release, new Promise<void>((resolve) => window.setTimeout(resolve, 5_000))]);
    }
    const banner = this.root.querySelector("[data-hold]") as HTMLElement | null; if (banner) banner.hidden = true;
    if (hold) {
      this.selectedSlot = undefined;
      const customer = this.root.querySelector("[data-customer]") as HTMLElement | null;
      const empty = this.root.querySelector("[data-customer-empty]") as HTMLElement | null;
      if (customer) customer.hidden = true;
      if (empty) empty.hidden = false;
      if (this.currentStep !== "confirmation") this.currentStep = "datetime";
      this.updateStepUI();
    }
  }

  private startCountdown(expiresAt: string, form: HTMLFormElement): void {
    const banner = form.querySelector("[data-hold]") as HTMLElement; const counter = form.querySelector("[data-countdown]") as HTMLElement; banner.hidden = false;
    const tick = () => {
      const seconds = Math.max(0, Math.ceil((Date.parse(expiresAt) - Date.now()) / 1000));
      counter.textContent = `${Math.floor(seconds / 60).toString().padStart(2, "0")}:${(seconds % 60).toString().padStart(2, "0")}`;
      if (seconds <= 0) {
        this.selectedSlot = undefined;
        void this.releasePendingHold().then(() => this.setStatus("El hold expiró. Elegí otro horario para continuar.", true));
      }
    };
    tick(); if (this.countdownTimer) window.clearInterval(this.countdownTimer); this.countdownTimer = window.setInterval(tick, 1000);
  }

  private renderConfirmation(): void {
    const detail = this.root.querySelector("[data-confirmation-detail]") as HTMLElement | null;
    const locale = this.getAttribute("locale") ?? "es-ES";
    if (!detail) return;
    const series = this.recurringCheckout?.series;
    if (series) {
      const selected = series.occurrences.filter((occurrence) => occurrence.status !== "cancelled" && occurrence.status !== "unavailable");
      const first = selected[0];
      const dates = first ? new Intl.DateTimeFormat(locale, { dateStyle: "full", timeStyle: "short", timeZone: series.timezone }).format(new Date(first.startAt)) : "";
      const currency = series.currency ?? this.recurringPreview?.currency ?? "EUR";
      const total = series.totalCents ?? this.recurringPreview?.totalCents ?? 0;
      detail.innerHTML = `<strong>Serie de ${selected.length} ocurrencia${selected.length === 1 ? "" : "s"}</strong><br>${escapeHtml(dates)}${selected.length > 1 ? " y fechas posteriores" : ""}<br>Total: ${this.renderPriceTooltip(total, currency, this.recurringPreview?.pricing ?? null, "Total de la serie")}<br>Serie: <strong>${escapeHtml(series.id.slice(0, 8))}</strong>`;
      this.bindPriceTooltips(detail);
      return;
    }
    if (this.lastBooking) {
      const service = this.services.find((item) => item.id === this.lastBooking?.serviceId);
      const date = new Intl.DateTimeFormat(locale, { dateStyle: "full", timeStyle: "short" }).format(new Date(this.lastBooking.startAt));
      const total = this.lastBooking.priceCents + (this.lastBooking.taxCents ?? 0);
      detail.innerHTML = `<strong>${escapeHtml(service?.name ?? "Reserva")}</strong><br>${escapeHtml(date)}<br>Total: ${this.renderPriceTooltip(total, this.lastBooking.currency, this.oneOffQuote, "Total de la reserva")}<br>Código: <strong>${escapeHtml(this.lastBooking.id.slice(0, 8))}</strong>`;
      this.bindPriceTooltips(detail);
    }
  }

  private renderRecurringPayment(): void {
    const content = this.root.querySelector("[data-payment-content]") as HTMLElement | null;
    const checkout = this.recurringCheckout;
    if (!content || !checkout) return;
    const state = checkout.series.paymentState ?? "pending";
    if (state === "paid") {
      const currency = checkout.series.currency ?? this.recurringPreview?.currency ?? "EUR";
      const total = checkout.series.totalCents ?? 0;
      content.innerHTML = `<div class="payment-link"><strong>Pago aprobado</strong><span>Las ${checkout.series.occurrences.length} ocurrencias de la serie quedaron confirmadas.</span><span>Total: ${this.renderPriceTooltip(total, currency, this.recurringPreview?.pricing ?? null, "Total de la serie")}</span></div>`;
      this.bindPriceTooltips(content);
      return;
    }
    const refreshLabel = this.paymentRefreshing ? "Consultando…" : "Ya pagué · actualizar";
    const refreshState = this.paymentRefreshing ? " disabled aria-busy=\"true\"" : "";
    if (state === "failed") {
      content.innerHTML = `<div class="payment-link failed"><strong>El pago no fue aprobado</strong><span>La serie se canceló y, si corresponde, quedó registrada una solicitud de devolución pendiente de aprobación administrativa.</span></div><button type="button" class="primary-button" data-action="payment-refresh"${refreshState}>${refreshLabel}</button>`;
    } else {
      const checkoutUrl = checkout.payment?.checkoutUrl;
      const currency = checkout.payment?.currency ?? checkout.series.currency ?? this.recurringPreview?.currency ?? "EUR";
      const total = checkout.payment?.amountCents ?? checkout.series.totalCents ?? 0;
      content.innerHTML = `<div class="payment-link"><strong>Pago único de la serie</strong><span>Se cobrará ${this.renderPriceTooltip(total, currency, this.recurringPreview?.pricing ?? null, "Total de la serie")} una sola vez por todas las fechas.</span>${checkoutUrl ? `<a href="${escapeHtml(checkoutUrl)}" target="_blank" rel="noreferrer">Abrir Mercado Pago</a>` : ""}<small>Después de pagar, esta pantalla actualizará el estado. Las citas permanecen pendientes hasta la aprobación del webhook.</small></div><button type="button" class="primary-button" data-action="payment-refresh"${refreshState}>${refreshLabel}</button>`;
    }
    content.querySelector('[data-action="payment-refresh"]')?.addEventListener("click", () => void this.refreshRecurringPayment(true));
  }

  private renderReviewPayment(): void {
    const content = this.root.querySelector("[data-payment-content]") as HTMLElement | null;
    const form = this.root.querySelector("form") as HTMLFormElement | null;
    if (!content || !form) return;
    const previousCoupon = form.querySelector<HTMLInputElement>("[data-review-coupon]")?.value ?? "";
    const preview = this.recurringPreview;
    if (preview) {
      const selected = this.selectedRecurringOccurrences().filter((occurrence) => occurrence.include);
      const selectedRows = preview.occurrences.filter((occurrence) => selected.some((item) => item.sequence === occurrence.sequence));
      const rows = selectedRows.map((occurrence) => {
        const totalCents = occurrence.totalCents ?? occurrence.pricing?.totalCents ?? occurrence.amountCents + occurrence.taxCents;
        const date = new Intl.DateTimeFormat(this.getAttribute("locale") ?? "es-ES", { dateStyle: "medium", timeStyle: "short", timeZone: preview.timezone }).format(new Date(occurrence.startAt));
        return `<li><span>${escapeHtml(date)}</span><strong>${this.renderPriceTooltip(totalCents, occurrence.currency, occurrence.pricing ?? null, `Ocurrencia ${occurrence.sequence + 1}`)}</strong></li>`;
      }).join("");
      content.innerHTML = `<div class="review-card"><h4>Revisión de tu serie</h4><p class="review-note">Se crearán ${selectedRows.length} ocurrencias después de validar disponibilidad, precio y cupón en el servidor.</p><ul class="review-lines">${rows || "<li>Elegí al menos una ocurrencia.</li>"}</ul><div class="review-total"><span>Total de la serie</span><strong>${this.recurringPricingLoading ? '<span class="spinner" aria-hidden="true"></span> Recalculando…' : this.renderPriceTooltip(preview.totalCents, preview.currency, preview.pricing ?? null, "Total de la serie")}</strong></div><label class="review-coupon">Cupón (opcional)<input name="couponCode" data-review-coupon autocomplete="off" placeholder="Código de cupón" value="${escapeHtml(previousCoupon)}" /></label><p class="review-note">El cupón cuenta una sola vez por esta serie y su descuento se distribuye en el detalle de cada ocurrencia.</p><p class="review-note">Método configurado: ${this.servicePaymentMode() === "prepay" ? "checkout alojado" : "pago en el local"}.</p></div>`;
    } else if (this.selectedSlot) {
      const quote = this.oneOffQuote ?? this.selectedSlot.quote ?? null;
      const service = this.services.find((item) => item.id === this.serviceIdFromForm());
      const date = new Intl.DateTimeFormat(this.getAttribute("locale") ?? "es-ES", { dateStyle: "full", timeStyle: "short", timeZone: this.selectedSlot.timezone ?? undefined }).format(new Date(this.selectedSlot.startAt));
      const total = quote?.totalCents ?? service?.priceCents ?? 0;
      const currency = quote?.currency ?? service?.currency ?? "EUR";
      content.innerHTML = `<div class="review-card"><h4>Revisión de tu reserva</h4><ul class="review-lines"><li><span>Servicio</span><strong>${escapeHtml(service?.name ?? "Reserva")}</strong></li><li><span>Fecha y hora</span><strong>${escapeHtml(date)}</strong></li><li><span>Profesional / sede</span><strong>${escapeHtml(this.selectedSlot.providerName ?? "Cualquier profesional")} · ${escapeHtml(this.selectedSlot.locationName ?? "Cualquier sede")}</strong></li></ul><div class="review-total"><span>Total</span><strong>${this.oneOffQuoteLoading ? '<span class="spinner" aria-hidden="true"></span> Recalculando…' : this.renderPriceTooltip(total, currency, quote, "Total de la reserva")}</strong></div><label class="review-coupon">Cupón (opcional)<input name="couponCode" data-review-coupon autocomplete="off" placeholder="Código de cupón" value="${escapeHtml(previousCoupon)}" /></label><p class="review-note">El importe final se vuelve a validar antes de crear el hold. El checkout, si corresponde, será alojado por el proveedor de pagos.</p></div>`;
    } else {
      content.innerHTML = '<div class="slots-empty">Primero elegí un horario para revisar la reserva.</div>';
    }
    this.bindPriceTooltips(content);
  }

  private renderPayment(): void {
    if (this.recurringCheckout) {
      this.renderRecurringPayment();
      return;
    }
    const content = this.root.querySelector("[data-payment-content]") as HTMLElement | null;
    const checkout = this.bookingCheckout;
    if (!content) return;
    if (!checkout) {
      this.renderReviewPayment();
      return;
    }
    const paymentStatus = this.bookingPaymentStatus?.payment?.status ?? checkout.payment.status;
    const linkStatus = this.bookingPaymentStatus?.link.status ?? checkout.payment.status;
    const bookingStatus = this.bookingPaymentStatus?.bookingStatus ?? checkout.booking.status;
    const refreshLabel = this.paymentRefreshing ? "Consultando…" : "Ya pagué · actualizar";
    const refreshState = this.paymentRefreshing ? " disabled aria-busy=\"true\"" : "";
    if (paymentStatus === "paid" && bookingStatus === "confirmed") {
      content.innerHTML = `<div class="payment-link"><strong>Pago aprobado</strong><span>Tu reserva quedó confirmada.</span></div>`;
    } else if (bookingStatus === "cancelled" || ["failed", "cancelled", "expired"].includes(paymentStatus) || ["cancelled", "expired"].includes(linkStatus)) {
      content.innerHTML = `<div class="payment-link failed"><strong>No se pudo confirmar la reserva</strong><span>${bookingStatus === "cancelled" && paymentStatus === "paid" ? "El pago llegó cuando el horario ya no estaba retenido. La devolución quedó registrada para revisión administrativa." : "El enlace de pago ya no está disponible o el pago fue rechazado."}</span></div><button type="button" class="primary-button" data-action="payment-refresh"${refreshState}>${refreshLabel}</button>`;
    } else {
      const checkoutUrl = checkout.payment.checkoutUrl;
      const checkoutQuote = checkout.quote ?? this.oneOffQuote;
      content.innerHTML = `<div class="payment-link"><strong>Pago único de la reserva</strong><span>Se cobrará ${this.renderPriceTooltip(checkout.payment.amountCents, checkout.payment.currency, checkoutQuote, "Total de la reserva")} una sola vez.</span>${checkoutUrl ? `<a href="${escapeHtml(checkoutUrl)}" target="_blank" rel="noreferrer">Abrir Mercado Pago</a>` : "<small>Estamos preparando el enlace de pago.</small>"}<small>La reserva queda pendiente hasta que Mercado Pago apruebe el cobro y el webhook confirme el horario.</small></div><button type="button" class="primary-button" data-action="payment-refresh"${refreshState}>${refreshLabel}</button>`;
    }
    content.querySelector('[data-action="payment-refresh"]')?.addEventListener("click", () => void this.refreshBookingPayment(true));
  }

  private startRecurringPaymentPolling(): void {
    if (!this.recurringCheckout?.series.id || !this.client) return;
    if (this.paymentPollTimer) window.clearInterval(this.paymentPollTimer);
    this.paymentPollAttempts = 0;
    this.paymentPollTimer = window.setInterval(() => void this.refreshRecurringPayment(), 5_000);
    void this.refreshRecurringPayment();
  }

  private async refreshRecurringPayment(manual = false): Promise<void> {
    const seriesId = this.recurringCheckout?.series.id;
    if (!seriesId || !this.client) {
      this.setStatus("No pudimos identificar la serie de pago. Volvé a iniciar la reserva.", true);
      return;
    }
    if (this.paymentRefreshing) return;
    if (this.paymentPollAttempts >= 24) {
      if (this.paymentPollTimer) window.clearInterval(this.paymentPollTimer);
      this.paymentPollTimer = undefined;
      if (!manual) return;
      // A manual click starts a fresh bounded polling window after the
      // background poller has stopped.
      this.paymentPollAttempts = 0;
    }
    this.paymentRefreshing = true;
    this.setStatus("Consultando el estado del pago en Kuroo…", false, true);
    this.updateStepUI();
    this.paymentPollAttempts += 1;
    try {
      const series = await this.client.recurringSeriesDetail(seriesId);
      this.recurringCheckout = { ...this.recurringCheckout!, series };
      if (series.paymentState === "paid") {
        if (this.paymentPollTimer) window.clearInterval(this.paymentPollTimer);
        this.paymentPollTimer = undefined;
        this.currentStep = "confirmation";
        this.setStatus("Pago aprobado. Todas las citas de la serie quedaron confirmadas.");
      } else if (series.paymentState === "failed") {
        if (this.paymentPollTimer) window.clearInterval(this.paymentPollTimer);
        this.paymentPollTimer = undefined;
        this.setStatus("El pago no fue aprobado. La serie quedó cancelada.", true);
      } else {
        this.setStatus("Mercado Pago todavía no confirmó el pago. Podés volver a intentar en unos segundos.");
      }
    } catch (error) {
      if (!isAbortError(error)) this.setStatus("No pudimos consultar el pago ahora. Podés volver a intentar.", true);
    } finally {
      this.paymentRefreshing = false;
      this.updateStepUI();
    }
  }

  private startBookingPaymentPolling(): void {
    if (!this.bookingCheckout?.paymentLink?.token || !this.client) return;
    if (this.paymentPollTimer) window.clearInterval(this.paymentPollTimer);
    this.paymentPollAttempts = 0;
    this.paymentPollTimer = window.setInterval(() => void this.refreshBookingPayment(), 5_000);
    void this.refreshBookingPayment();
  }

  private async refreshBookingPayment(manual = false): Promise<void> {
    const token = this.bookingCheckout?.paymentLink?.token;
    if (!token || !this.client) {
      this.setStatus("No pudimos identificar el enlace de pago. Volvé a iniciar la reserva.", true);
      return;
    }
    if (this.paymentRefreshing) return;
    if (this.paymentPollAttempts >= 24) {
      if (this.paymentPollTimer) window.clearInterval(this.paymentPollTimer);
      this.paymentPollTimer = undefined;
      if (!manual) return;
      this.paymentPollAttempts = 0;
    }
    this.paymentRefreshing = true;
    this.setStatus("Consultando el estado del pago en Kuroo…", false, true);
    this.updateStepUI();
    this.paymentPollAttempts += 1;
    try {
      const status = await this.client.paymentLinkStatus(token);
      this.bookingPaymentStatus = status;
      const paymentStatus = status.payment?.status ?? this.bookingCheckout?.payment.status;
      const bookingStatus = status.bookingStatus ?? this.bookingCheckout?.booking.status;
      if (paymentStatus === "paid" && bookingStatus === "confirmed") {
        if (this.paymentPollTimer) window.clearInterval(this.paymentPollTimer);
        this.paymentPollTimer = undefined;
        const booking = this.bookingCheckout!.booking;
        this.lastBooking = { ...booking, status: "confirmed" };
        this.currentStep = "confirmation";
        this.setStatus("Pago aprobado. Tu reserva quedó confirmada.");
        this.dispatchEvent(new CustomEvent("kuroo:booking-created", { bubbles: true, composed: true, detail: this.lastBooking }));
        await this.loadSlots({ preserveStatus: true });
        this.currentStep = "confirmation";
      } else if (bookingStatus === "cancelled" || ["failed", "cancelled", "expired"].includes(paymentStatus ?? "") || ["cancelled", "expired"].includes(status.link.status)) {
        if (this.paymentPollTimer) window.clearInterval(this.paymentPollTimer);
        this.paymentPollTimer = undefined;
        this.setStatus(paymentStatus === "paid" ? "El pago llegó tarde y la reserva fue cancelada. Administración gestionará la devolución." : "El pago no fue aprobado. La reserva quedó cancelada.", true);
      } else {
        this.setStatus("Mercado Pago todavía no confirmó el pago. Podés volver a intentar en unos segundos.");
      }
    } catch (error) {
      if (!isAbortError(error)) this.setStatus("No pudimos consultar el pago ahora. Podés volver a intentar.", true);
    } finally {
      this.paymentRefreshing = false;
      this.updateStepUI();
    }
  }

  private async submit(event: SubmitEvent): Promise<void> {
    event.preventDefault();
    const form = this.root.querySelector("form") as HTMLFormElement; const submit = form.querySelector('[data-action="continue"]') as HTMLButtonElement; const customer = form.querySelector("[data-customer]") as HTMLElement;
    if (this.currentStep === "confirmation") return;
    if (this.currentStep === "payment") {
      if (this.bookingCheckout) await this.refreshBookingPayment(true);
      else if (this.recurringCheckout) await this.refreshRecurringPayment(true);
      else await this.submitReview(form, submit);
      return;
    }
    if (!this.serviceIdFromForm()) { this.setStatus("Elegí un servicio para continuar.", true); return; }
    if (!this.selectedSlot) { this.setStatus("Elegí un horario disponible.", true); return; }
    const recurrenceToggle = form.elements.namedItem("recurringEnabled") as HTMLInputElement | null;
    if (this.currentStep === "datetime" && recurrenceToggle?.checked) {
      this.applyWeeklyAnchorDefault(form);
      this.currentStep = "recurrence";
      this.updateStepUI();
      this.setStatus("Verificando disponibilidad y precio de las cuatro ocurrencias…", false, true);
      void this.refreshRecurringPreviewForSelection(form);
      return;
    }
    if (this.currentStep === "recurrence") {
      if (!this.serviceRecurrence()?.enabled) { this.setStatus("La recurrencia no está habilitada para este servicio.", true); return; }
      if (!this.recurringPreview) {
        await this.refreshRecurringPreviewForSelection(form);
        return;
      }
      if (!this.validateRecurringOccurrenceInputs()) return;
      this.currentStep = "information";
      this.updateStepUI();
      this.setStatus("Revisá las fechas y completá tus datos para continuar a Revisión y pago.");
      return;
    }
    if (this.currentStep === "datetime") {
      this.currentStep = "information";
      this.updateStepUI();
      this.setStatus("Completá tus datos para revisar el precio y confirmar la reserva.");
      if (!this.oneOffQuote) void this.refreshOneOffQuote(form);
      return;
    }
    if (this.currentStep === "information" && !this.pendingHold) {
      const values = Object.fromEntries(new FormData(form).entries());
      const email = String(values.email ?? "").trim();
      const firstName = String(values.firstName ?? "").trim();
      const consent = values.consent === "on";
      if (!email || !firstName || !consent) { this.setStatus("Completá nombre, email y consentimiento para continuar.", true); return; }
      if (this.recurringPreview && !this.validateRecurringOccurrenceInputs()) return;
      this.currentStep = "payment";
      this.updateStepUI();
      this.setStatus("Revisá el detalle final. Todavía no se creó ningún hold ni checkout.");
      if (!this.recurringPreview && !this.oneOffQuote) void this.refreshOneOffQuote(form);
      return;
    }
    if (!this.pendingHold) {
      if (this.currentStep === "information" && this.recurringPreview) {
        if (!this.validateRecurringOccurrenceInputs()) return;
        const values = Object.fromEntries(new FormData(form).entries()); const email = String(values.email ?? "").trim(); const firstName = String(values.firstName ?? "").trim(); const consent = values.consent === "on";
        if (!email || !firstName || !consent) { this.setStatus("Completá nombre, email y consentimiento para crear la serie.", true); return; }
        submit.disabled = true; this.setStatus("Creando la serie y reservando temporalmente todas las fechas…", false, true);
        try {
          const slot = this.selectedSlot;
          const rule = this.recurringRuleFromForm(form);
          const checkout = await this.client!.checkoutRecurringSeries({
            serviceId: this.serviceIdFromForm(), providerId: slot.providerId ?? null, locationId: slot.locationId ?? null,
             timezone: slot.timezone ?? "UTC", startsAt: slot.startAt, count: this.recurringPreview.occurrences.length,
            rule, persons: Math.max(1, Number(values.persons ?? 1) || 1), participants: this.recurringParticipantsFromForm(form), extras: this.selectedExtrasFromForm(form, true), couponCode: String(values.couponCode ?? "").trim() || undefined,
            occurrences: this.selectedRecurringOccurrences(), customer: { email, firstName, lastName: String(values.lastName ?? "").trim() || undefined, phone: String(values.phone ?? "").trim() || undefined, consent: true },
            notes: String(values.notes ?? "").trim() || undefined, paymentMethod: this.serviceRecurrence()?.paymentMode === "prepay" ? "mercadopago" : "on_site", idempotencyKey: randomIdempotency("public-recurring-checkout")
          });
          this.recurringCheckout = checkout;
          this.dispatchEvent(new CustomEvent("kuroo:recurring-series-created", { bubbles: true, composed: true, detail: checkout }));
          if (checkout.payment) {
            this.currentStep = "payment";
            this.startRecurringPaymentPolling();
            this.setStatus("Serie creada. Completá el pago único en Mercado Pago.");
          } else {
            this.lastBooking = checkout.bookings[0];
            this.currentStep = "confirmation";
            this.setStatus(`Serie confirmada con ${checkout.bookings.length} ocurrencia${checkout.bookings.length === 1 ? "" : "s"}.`);
          }
          submit.disabled = false;
          this.updateStepUI();
        } catch (error) {
          submit.disabled = false;
          this.setStatus(this.errorMessage(error), true);
          this.dispatchEvent(new CustomEvent("kuroo:booking-error", { bubbles: true, composed: true, detail: { stage: "recurring-checkout", error } }));
        }
        return;
      }
      submit.disabled = true; this.setStatus("Reservando temporalmente ese horario…", false, true);
      try {
        const slot = this.selectedSlot; const result = await this.client!.holdSlot({ slotKey: slot.slotKey, startAt: slot.startAt, endAt: slot.endAt, serviceId: this.serviceIdFromForm(), providerId: slot.providerId ?? undefined, locationId: slot.locationId ?? undefined, capacity: Number((form.elements.namedItem("persons") as HTMLInputElement)?.value || 1), idempotencyKey: randomIdempotency("public-hold") });
        this.pendingHold = { ...result, startAt: slot.startAt, endAt: slot.endAt, providerId: slot.providerId ?? undefined, locationId: slot.locationId ?? undefined };
        customer.hidden = false;
        this.currentStep = "information";
        submit.textContent = "Confirmar reserva"; submit.disabled = false;
        this.startCountdown(result.expiresAt, form); this.updateStepUI(); this.setStatus("Completá tus datos y confirmá antes de que expire el hold."); this.dispatchEvent(new CustomEvent("kuroo:slot-held", { bubbles: true, composed: true, detail: result }));
      } catch (error) { submit.disabled = false; this.setStatus(this.errorMessage(error), true); this.dispatchEvent(new CustomEvent("kuroo:booking-error", { bubbles: true, composed: true, detail: { stage: "hold", error } })); }
      return;
    }
    const values = Object.fromEntries(new FormData(form).entries()); const email = String(values.email ?? "").trim(); const firstName = String(values.firstName ?? "").trim(); const consent = values.consent === "on";
    if (!email || !firstName || !consent) { this.setStatus("Completá nombre, email y consentimiento para confirmar.", true); return; }
    submit.disabled = true; this.setStatus("Confirmando tu reserva…", false, true);
    try {
      const result = await this.client!.createBooking({ holdId: this.pendingHold.holdId, slotKey: this.pendingHold.slotKey, serviceId: this.serviceIdFromForm(), providerId: this.pendingHold.providerId, locationId: this.pendingHold.locationId, startAt: this.pendingHold.startAt, endAt: this.pendingHold.endAt, persons: Math.max(1, Number(values.persons ?? 1) || 1), idempotencyKey: randomIdempotency("public-booking"), notes: String(values.notes ?? "").trim() || undefined, extras: this.selectedExtrasFromForm(form), couponCode: this.couponCodeFromForm(form), paymentMethod: this.servicePaymentMode() === "prepay" ? "mercadopago" : "on_site", customer: { email, firstName, lastName: String(values.lastName ?? "").trim() || undefined, phone: String(values.phone ?? "").trim() || undefined, consent: true } });
      this.pendingHold = undefined; if (this.countdownTimer) window.clearInterval(this.countdownTimer); const banner = form.querySelector("[data-hold]") as HTMLElement; banner.hidden = true;
      if ("booking" in result) {
        this.bookingCheckout = result;
        this.bookingPaymentStatus = undefined;
        this.currentStep = "payment";
        this.startBookingPaymentPolling();
        this.setStatus("Reserva creada. Completá el pago único en Mercado Pago.");
        this.dispatchEvent(new CustomEvent("kuroo:booking-checkout-created", { bubbles: true, composed: true, detail: result }));
      } else {
        this.lastBooking = result;
        this.currentStep = "confirmation"; this.updateStepUI(); submit.textContent = "Reserva confirmada";
        const confirmation = `Reserva confirmada. Código ${result.id.slice(0, 8)}.`; this.setStatus(confirmation); this.dispatchEvent(new CustomEvent("kuroo:booking-created", { bubbles: true, composed: true, detail: result })); await this.loadSlots({ preserveStatus: true }); this.currentStep = "confirmation"; this.updateStepUI(); submit.textContent = "Reserva confirmada"; this.setStatus(confirmation);
      }
      this.updateStepUI();
    } catch (error) { submit.disabled = false; this.setStatus(this.errorMessage(error), true); this.dispatchEvent(new CustomEvent("kuroo:booking-error", { bubbles: true, composed: true, detail: { stage: "booking", error } })); }
  }

  private async submitReview(form: HTMLFormElement, submit: HTMLButtonElement): Promise<void> {
    const slot = this.selectedSlot;
    if (!slot || !this.client) return;
    const values = Object.fromEntries(new FormData(form).entries());
    const email = String(values.email ?? "").trim();
    const firstName = String(values.firstName ?? "").trim();
    const consent = values.consent === "on";
    if (!email || !firstName || !consent) { this.setStatus("Completá nombre, email y consentimiento para confirmar.", true); return; }
    if (this.recurringPreview) {
      if (!this.validateRecurringOccurrenceInputs()) return;
      const selections = this.selectedRecurringOccurrences();
      if (!selections.some((occurrence) => occurrence.include)) { this.setStatus("Conservá al menos una ocurrencia para continuar.", true); return; }
      if (this.recurringPreview.occurrences.some((occurrence) => occurrence.status === "unavailable" && selections.some((selection) => selection.sequence === occurrence.sequence && selection.include))) { this.setStatus("Hay ocurrencias sin disponibilidad. Quitalas o modificá sus datos antes de continuar.", true); return; }
      submit.disabled = true;
      this.setStatus("Validando nuevamente disponibilidad, precio y cupón antes de crear la serie…", false, true);
      try {
        const checkout = await this.client.checkoutRecurringSeries({
          serviceId: this.serviceIdFromForm(), providerId: slot.providerId ?? null, locationId: slot.locationId ?? null,
          timezone: slot.timezone ?? "UTC", startsAt: slot.startAt, count: this.recurringCountFromForm(form, this.recurringPreview.occurrences.length),
          rule: this.recurringRuleFromForm(form), persons: Math.max(1, Number(values.persons ?? 1) || 1), participants: this.recurringParticipantsFromForm(form),
          extras: this.selectedExtrasFromForm(form, true), couponCode: this.couponCodeFromForm(form), occurrences: selections,
          customer: { email, firstName, lastName: String(values.lastName ?? "").trim() || undefined, phone: String(values.phone ?? "").trim() || undefined, consent: true },
          notes: String(values.notes ?? "").trim() || undefined, paymentMethod: this.serviceRecurrence()?.paymentMode === "prepay" ? "mercadopago" : "on_site", idempotencyKey: randomIdempotency("public-recurring-checkout")
        });
        this.recurringCheckout = checkout;
        this.dispatchEvent(new CustomEvent("kuroo:recurring-series-created", { bubbles: true, composed: true, detail: checkout }));
        if (checkout.payment) {
          this.currentStep = "payment";
          this.startRecurringPaymentPolling();
          this.setStatus("Serie creada. Completá el checkout alojado para confirmar todas las fechas.");
        } else {
          this.lastBooking = checkout.bookings[0];
          this.currentStep = "confirmation";
          this.setStatus(`Serie confirmada con ${checkout.bookings.length} ocurrencia${checkout.bookings.length === 1 ? "" : "s"}.`);
        }
      } catch (error) {
        if (error instanceof KurooApiError && error.code === "QUOTE_CHANGED") {
          this.currentStep = "recurrence";
          this.setStatus("El precio cambió mientras confirmábamos. Actualizamos la cotización; revisala y confirmá otra vez.", true);
          await this.refreshRecurringPreviewForSelection(form);
        } else {
          this.setStatus(this.errorMessage(error), true);
        }
        this.dispatchEvent(new CustomEvent("kuroo:booking-error", { bubbles: true, composed: true, detail: { stage: "recurring-checkout", error } }));
      } finally {
        submit.disabled = false;
        this.updateStepUI();
      }
      return;
    }

    submit.disabled = true;
    this.setStatus("Verificando disponibilidad, precio y cupón antes de reservar…", false, true);
    try {
      const service = this.services.find((item) => item.id === this.serviceIdFromForm());
      if (!service) throw new Error("SERVICE_NOT_FOUND");
      const persons = Math.max(1, Number(values.persons ?? 1) || 1);
      const endAt = this.oneOffEndAt(form, slot, service);
      const quote = await this.client.quoteService({
        serviceId: service.id, providerId: slot.providerId ?? null, locationId: slot.locationId ?? null,
        startAt: slot.startAt, endAt, persons, extras: this.selectedExtrasFromForm(form), couponCode: this.couponCodeFromForm(form), slotKey: slot.slotKey,
        timezone: slot.timezone ?? "UTC", idempotencyKey: randomIdempotency("public-final-quote")
      });
      this.oneOffQuote = quote;
      const result = await this.client.holdSlot({
        slotKey: slot.slotKey, startAt: slot.startAt, endAt, serviceId: service.id, providerId: slot.providerId ?? undefined, locationId: slot.locationId ?? undefined,
        persons, capacity: persons, extras: this.selectedExtrasFromForm(form), quoteId: quote.id, idempotencyKey: randomIdempotency("public-hold")
      });
      this.pendingHold = { ...result, startAt: slot.startAt, endAt, providerId: slot.providerId ?? undefined, locationId: slot.locationId ?? undefined };
      const bookingResult = await this.client.createBooking({
        holdId: result.holdId, slotKey: slot.slotKey, serviceId: service.id, providerId: slot.providerId ?? undefined, locationId: slot.locationId ?? undefined,
        startAt: slot.startAt, endAt, persons, quoteId: quote.id, idempotencyKey: randomIdempotency("public-booking"), notes: String(values.notes ?? "").trim() || undefined,
        extras: this.selectedExtrasFromForm(form), couponCode: this.couponCodeFromForm(form), paymentMethod: this.servicePaymentMode() === "prepay" ? "mercadopago" : "on_site",
        customer: { email, firstName, lastName: String(values.lastName ?? "").trim() || undefined, phone: String(values.phone ?? "").trim() || undefined, consent: true }
      });
      this.pendingHold = undefined;
      if ("booking" in bookingResult) {
        this.bookingCheckout = bookingResult;
        this.bookingPaymentStatus = undefined;
        this.currentStep = "payment";
        this.startBookingPaymentPolling();
        this.setStatus("Reserva creada. Completá el checkout alojado para confirmarla.");
        this.dispatchEvent(new CustomEvent("kuroo:booking-checkout-created", { bubbles: true, composed: true, detail: bookingResult }));
      } else {
        this.lastBooking = bookingResult;
        this.currentStep = "confirmation";
        this.setStatus(`Reserva confirmada. Código ${bookingResult.id.slice(0, 8)}.`);
        this.dispatchEvent(new CustomEvent("kuroo:booking-created", { bubbles: true, composed: true, detail: bookingResult }));
      }
    } catch (error) {
      await this.releasePendingHold();
      this.setStatus(this.errorMessage(error), true);
      this.dispatchEvent(new CustomEvent("kuroo:booking-error", { bubbles: true, composed: true, detail: { stage: "review", error } }));
    } finally {
      submit.disabled = false;
      this.updateStepUI();
    }
  }

  private applyWeeklyAnchorDefault(form: HTMLFormElement): void {
    if (this.weeklyDaysCustomized || !this.selectedSlot) return;
    const weekday = new Intl.DateTimeFormat("en-US", { weekday: "short", timeZone: this.selectedSlot.timezone ?? "UTC" }).format(new Date(this.selectedSlot.startAt));
    const dayByName: Record<string, number> = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
    const selectedDay = dayByName[weekday];
    if (selectedDay === undefined) return;
    form.querySelectorAll<HTMLInputElement>('input[name="weeklyDay"]').forEach((input) => { input.checked = Number(input.value) === selectedDay; });
  }
}

export function defineKurooWidgets(): void {
  if (!customElements.get(elementName)) customElements.define(elementName, KurooBookingElement);
}

defineKurooWidgets();
